import { useCallback, useEffect, useId, useMemo, useRef, useState, useSyncExternalStore, type DragEvent, type MouseEvent as ReactMouseEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import type { Block, BlockType } from '@/api/courses';
import { vocabApi } from '@/api/vocab';
import { classifyWordAddError, runAddQueue } from '@/lib/addWordsQueue';
import { AudioPlayButton } from '@/components/ui/AudioPlayButton';
import { AudioPlayer } from '@/components/ui/AudioPlayer';
import { preloadWords } from '@/lib/wordAudio';
import { isFeatureOn } from '@/lib/appConfig';
import { lessonAnswerMatch } from '@/lib/normalizeAnswer';
import { gapBank, uniqueWords } from '@/lib/gapBank';
import { parseGapTemplate, type GapSegment } from '@/lib/gapTemplate';
import { tableLayout, tableModel, type TableCell, type TableFillContent } from '@/lib/tableFill';
import { tfNotGivenAllowed } from '@/lib/trueFalse';
import {
  openItemsAnswerOk,
  openItemsFilled,
  openItemsOf,
  parseOpenItemsAnswer,
  type OpenItem,
  type OpenItemsContent,
} from '@/lib/openItems';
import { buildQuizAnswer } from '@/lib/quizTimer';
import { clock } from '@/lib/clock';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { useConfirm } from '@/components/ui/ConfirmProvider';
import { Pill } from '@/components/ui/Pill';
import { Watermark, WatermarkLayer } from '@/components/protection/Watermark';
import { YouTubePlayer } from '@/components/media/YouTubePlayer';
import { FileVideo } from '@/components/media/FileVideo';
import { cn } from '@/lib/utils';
import {
  allPairsCorrect,
  allSettled,
  bankWords,
  canCheck,
  distinctPairs,
  distinctWords,
  emptyRows,
  gradeRows,
  matchTap,
  resetRow,
  rightOwner,
  rowLocked,
  type RowState,
} from '@/lib/matching';
import { emptyResults, recordItem, type ItemResults } from '@/lib/blockResult';
import {
  dragToken,
  emptyPlacement,
  itemsAt,
  move as movePlaced,
  occupant,
  readDragToken,
  tapBank,
  tapItem,
  tapPlace,
  tapRow,
  tapSlot,
  type Placement,
} from '@/lib/tapPlace';
import { sanitizeHtml } from '@/lib/sanitizeHtml';
import { titleHtml, titleText } from '@/lib/blockTitle';
import { usePreviewSandbox } from '@/components/lesson/previewSandbox';
import { extractYouTubeId } from '@/lib/youtube';
import { driveFileId, drivePreviewUrl } from '@/lib/driveVideo';
import { InlineYouTube } from '@/components/media/InlineYouTube';
import { isStoreBuild } from '@/lib/platform';
import {
  MIN_RECORDING_SECONDS,
  essayCanSave,
  essayWordCount,
  savedOpenAnswerProblem,
  openAnswerRejection,
  openAnswerRejectionKey,
  recordingTooShort,
} from '@/lib/openAnswerMin';
import { createRecordingClock } from '@/lib/recordingClock';
import { recordingStarted } from '@/lib/recordingGuard';
import { apiErrorCode, apiErrorReason } from '@/lib/apiErrorCode';
import { forgetRecordingResult, recordingResultOf, subscribeRecordingResult } from '@/lib/recordingResult';
import { isUnexpectedStop, pickStopReason, stopReasonKey, type StopFlags, type StopReason } from '@/lib/stopReason';
import { watchMicrophone, type MicLoss } from '@/lib/micWatch';
import { recordingVerdict, type RecordingVerdict } from '@/lib/recordingIntegrity';
import { publishSendProgress, sendProgressOf, subscribeSendProgress } from '@/lib/sendProgress';
import { canMeasureAudio, measureBlobSeconds } from '@/lib/audioDuration';
import { probePlayable } from '@/lib/audioPlayable';
import { sniffBlob } from '@/lib/audioSniff';
import { reportBrokenRecording, type BrokenRecordingReason } from '@/lib/recordingBrokenReport';
import { attachMicLevel, createHintRun, type MicMeter, type RecordHint } from '@/lib/micLevel';
import { RecordLevelPanel } from '@/components/lesson/RecordLevelPanel';
import { MUTE_GRACE_MS } from '@/lib/micWatch';
import { keepScreenAwake } from '@/lib/wakeLock';
import { startBar } from '@/lib/recordingBar';
import { appendChunk, DRAFT_CHUNK_MS, dropDraft, openDraft, readDraft } from '@/lib/recordingDraft';
import { clearDraft, loadDraft, saveDraft } from '@/lib/answerDraft';
import { useAuthStore } from '@/stores/auth';
import { flush as flushOutbox, isOutboxItemStale, isOutboxStorageError, onOutboxSettled, queuedItem, removeQueued } from '@/lib/outbox';
import { lessonAudioId, loadLessonAudio, newClientId, type LessonAudioPayload } from '@/lib/lessonAudio';
import {
  markOfflineHintShown,
  markVoiceTaskSeen,
  recordBitrate,
  shouldHintOfflineRecords,
  takeRecordNotice,
} from '@/lib/offlineRecords';


/**
 * Block renderers — counterpart to admin's blockRegistry.
 * Each renderer takes a `Block` (with `type` + `content`) and renders the
 * student-facing UI. Interactive blocks call `onAnswer` so the lesson player
 * can ship results to progress-service.
 *
 * Keep these simple — they are the "view" half of the editor pair. The TЗ
 * has 26+ types; the ones not implemented here render via FallbackBlock.
 */

export type RenderProps = {
  block: Block;
  /**
   * Ответ ученика. Возвращает `true`, если сервер уже подтвердил приём, и
   * `false`, если ответ лёг в очередь и уйдёт позже — блок по этому значению
   * решает, писать «сохранено» или «ждёт отправки».
   *
   * `blob` — для голосового ответа. Ссылки на запись здесь НЕТ и быть не может:
   * пока запись не ушла на сервер, ссылки не существует. Поэтому наверх идёт
   * сам файл, а загрузкой занимается очередь — иначе при обрыве связи запись
   * пропадала совсем (см. задачу 120).
   */
  onAnswer?: (payload: {
    answer_data: string;
    is_correct: boolean;
    blob?: Blob;
    /**
     * Сколько насчитали часы записи, без пауз, и кто её остановил (12.09.2026,
     * договор `record-integrity.md` §2.1). Сервер сверяет это со звуком в файле:
     * без часов расхождение «наговорил 2:30 — в файле 0:20» ничем не видно.
     */
    clientSeconds?: number;
    stopReason?: string;
    /**
     * Имя записи и момент «Стоп» (02.10.2026, договор `audio-upload.md` §1). Рождаются вместе с записью и
     * едут с ней в очередь: по ним сервер узнаёт повтор той же записи и считает, сколько она пролежала.
     */
    clientId?: string;
    stoppedAt?: number;
  }) => void | Promise<boolean | void>;
  /**
   * The student's previously-submitted answer for this block, loaded on lesson
   * open so a page refresh RESTORES their work instead of showing it blank.
   * Individual renderers may use `answer_data` to rehydrate their UI; the shared
   * BlockView shows a "saved" indicator for every answered block.
   */
  saved?: { answer_data: string; is_correct: boolean };
  /**
   * Сервер назвал это задание незаполненным в отказе `409 open_answers_missing`.
   * Открытые задания показывают причину у себя, а не только общую фразу над разделом.
   */
  missing?: boolean;
  /**
   * Разбор ответов куратором или админом (`BlockView` mode='review'). Блок только
   * показывает ответ. Сейчас это важно одному сочинению: поле ввода внутри
   * `.review-inert` не прокручивается, и текст длиннее шести строк куратору не виден.
   */
  readOnly?: boolean;
};

type Renderer = (props: RenderProps) => ReactNode;

// RichText renders teacher-authored text safely: it preserves paragraphs and
// line breaks (a textarea's blank line becomes a real paragraph instead of
// collapsing into one run of text) and passes through any inline formatting
// (bold / italic / underline / colour) the editor emits. Same pipeline as the
// standalone Text block (textToHtml → sanitizeHtml). No-op when empty.
function RichText({ text, className }: { text?: string | null; className?: string }) {
  if (!text) return null;
  return (
    <div
      // `rt` — маркеры и отступы списков (index.css, 13.10.2026): preflight их обнуляет.
      className={`rt [&>p]:m-0 [&>p+p]:mt-2 ${className ?? ''}`}
      dangerouslySetInnerHTML={{ __html: sanitizeHtml(textToHtml(text)) }}
    />
  );
}

// Optional per-exercise task text (Edvibe-style), shown above the interactive
// area. No-op when the block has no instruction.
const Instruction = ({ text }: { text?: string }) =>
  <RichText text={text} className="font-medium text-on-surface" />;

// Aggregates per-item results for multi-item exercise blocks (many questions /
// sentences / pictures in ONE block). Reports the block answer once every item
// is answered. Single-item blocks keep the item's own raw answer_data (server
// drill-grader compatible); multi reports the boolean results array.
//
// ⚠️ ВОССТАНОВЛЕННЫЙ ПУНКТ СООБЩАЕТ СВОЙ РЕЗУЛЬТАТ С `send = false`. До 10.09.2026
// он молчал, и сборщик о нём не знал: ученик возвращался в урок, исправлял один
// пункт — и ответ блока не уходил вовсе, в базе оставалось прежнее «неверно».
// Правила сборки и проверка — lib/blockResult.ts.
//
// Результаты держим в ref, а не в состоянии: перерисовка от них не нужна, а
// отправка из функции-обновителя состояния срабатывала бы дважды в режиме
// разработки.
type ItemReport = (index: number, correct: boolean, data: string, send?: boolean) => void;
function useBlockResult(
  count: number,
  single: boolean,
  onAnswer?: (payload: { answer_data: string; is_correct: boolean }) => void,
): ItemReport {
  const results = useRef<ItemResults>(emptyResults(count));
  if (results.current.ok.length !== count) results.current = emptyResults(count);
  return (index, correct, data, send = true) => {
    const { next, payload } = recordItem(results.current, index, correct, data, single, send);
    results.current = next;
    if (payload) onAnswer?.(payload);
  };
}

// Parses a block's saved answer_data into per-item initial values so each item
// renderer can restore itself. Single-item blocks store the raw item value;
// multi-item blocks store a JSON array. Legacy answers (pre-restore) that stored
// a boolean array can't be turned back into values → we return [] and the block
// simply renders fresh under the "saved" indicator.
function savedItemValues(saved: RenderProps['saved'], single: boolean): (string | undefined)[] {
  if (!saved) return [];
  if (single) return [saved.answer_data];
  try {
    const arr = JSON.parse(saved.answer_data);
    if (Array.isArray(arr) && arr.some((x) => typeof x === 'string')) {
      return arr.map((x) => (typeof x === 'string' ? x : undefined));
    }
  } catch {
    /* not JSON — nothing to restore */
  }
  return [];
}

// Wraps each item of a multi-item block with a divider + "N / M" counter (shown
// only when there's more than one item), matching the Quiz/True-False layout.
const MultiItem = ({ index, total, children }: { index: number; total: number; children: ReactNode }) => (
  <div className={cn('space-y-2', index > 0 && 'border-t border-outline-variant/60 pt-4')}>
    {total > 1 && (
      <span className="text-xs font-semibold uppercase tracking-wide text-on-surface-variant">
        {index + 1} / {total}
      </span>
    )}
    {children}
  </div>
);

// ---- Media ----

/**
 * Общий каркас медиа-блока: описание → само медиа → подпись (договор
 * `constructor-v2.md` §3, решение владельца 12.09.2026).
 *
 * ⚠️ ЗАГОЛОВОК СЮДА НЕ ВХОДИТ. Его печатает обёртка раздела (`ex-head` в
 * `StudentLessonPlayerPage`) над ЛЮБЫМ блоком. Нарисуй его ещё и здесь — он появится
 * дважды; на этом уже обжигались с видео.
 *
 * Пустое поле, `null` или отсутствие ключа — не рисовать: старые блоки новых полей не
 * знают, миграции у них нет, и они обязаны остаться верными.
 *
 * ⚠️ ЭТО ФУНКЦИЯ, А НЕ КОМПОНЕНТ, И НАРОЧНО. В этом файле 47 компонентов, и на каждый
 * линтер выдаёт своё «вынесите компоненты в отдельный файл» (файл экспортирует ещё и
 * `renderBlock`). Порог предупреждений в проекте зафиксирован, а новый компонент поднял
 * бы счёт на единицу без всякой пользы: ни состояния, ни хуков здесь нет.
 */
const mediaFrame = (c: { description?: string | null; caption?: string | null }, media: ReactNode) => (
  <figure className="space-y-2">
    <RichText text={c.description} className="text-on-surface" />
    {media}
    {c.caption && (
      <figcaption className="whitespace-pre-wrap text-center text-sm text-on-surface-variant">{c.caption}</figcaption>
    )}
  </figure>
);

const ImageBlock: Renderer = ({ block }) => {
  const c = block.content as { url?: string; caption?: string; description?: string };
  if (!c.url) return null;
  return mediaFrame(
    c,
    <Watermark>
      <img
        src={c.url}
        // Подпись служит и описанием для чтеца с экрана — так было до появления
        // отдельного поля, и договор §3.1 этот смысл сохраняет.
        alt={c.caption ?? ''}
        className="w-full rounded-card"
        // P4.3: discourage casual right-click → "Save image as…"
        onContextMenu={(e) => e.preventDefault()}
        draggable={false}
      />
    </Watermark>,
  );
};

/**
 * Плеер урока: обложка → по нажатию сам ролик.
 *
 * ⚠️ ДВА РАЗНЫХ ПУТИ, И ОБА ВЫНУЖДЕННЫЕ.
 *
 * **На сайте** плеер разворачивается прямо здесь, на странице урока. Адрес
 * страницы — наш домен, YouTube такой принимает, всё просто.
 *
 * **В приложении** YouTube отвергает адрес, с которого открыт плеер, и отвечает
 * «Ошибка 153». Проверено на обоих внутренних адресах: `capacitor://localhost`
 * (сборки до 17-й) и `https://localhost` (сборка 26, 24.08.2026) — отвергает
 * оба. Значит плеер обязан открываться с нашего домена.
 *
 * Вставить нашу страницу `/embed/` в рамку мы уже пробовали дважды — и оба раза
 * падал внутренний браузерный движок приложения: сначала при открытии урока,
 * потом при нажатии «плей». Поэтому третий путь: **системное окно браузера**
 * поверх приложения. Это отдельный процесс — уронить приложение он не может,
 * а страница открывается с нашего домена, значит и 153 не будет. Возвращаемся
 * в урок одним нажатием «Готово».
 *
 * Обложку до нажатия оставляем в обоих случаях: в уроке до семи видеоблоков.
 */
// ⚠️ В ПРИЛОЖЕНИИ РОЛИК ОТКРЫВАЕТСЯ СНАРУЖИ, И ЭТО ВЫНУЖДЕННО.
//
// YouTube требует настоящий http(s)-источник. Получить его у приложения на iOS
// штатными средствами Capacitor НЕЛЬЗЯ: схему `https` система обслуживает сама и
// отдать её приложению не даёт, поэтому Capacitor молча возвращает `capacitor://`
// (проверено по исходникам, см. пояснение в capacitor.config.ts).
//
// 🔴 04.09.2026 я решил, что дело в имени хоста, собрал сборку 48 — и приложение
// перестало входить: источник стал `capacitor://app.onebilim.kz`, которого нет в
// списке разрешённых. Урок: проверять исходниками, а не настройкой.
//
// Пока не сделан отдельный экран-обёртка с нашей https-страницей, единственный
// рабочий путь — системное окно.
function EmbeddedPlayer({ videoId, title }: { videoId: string; title?: string }) {
  const [started, setStarted] = useState(false);

  // В приложении из магазина — простая вставка ПРЯМО В КАРТОЧКЕ: наш плеер
  // там не заводится (нет настоящего адреса страницы), а отдельное окно
  // закрывает урок и мешает отвечать на вопросы.
  if (isStoreBuild()) {
    // native — ветка video-native-webview (пробы 58–60): видеоблок урока играет в нативном виде, живой — один.
    return <InlineYouTube videoId={videoId} title={title} native />;
  }

  if (!started) {
    return (
      <button
        type="button"
        onClick={() => setStarted(true)}
        className="relative block aspect-video w-full overflow-hidden rounded-card bg-black"
        aria-label={title || 'video'}
      >
        <img
          src={`https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`}
          alt=""
          loading="lazy"
          className="h-full w-full object-cover opacity-90"
        />
        <span className="absolute inset-0 grid place-items-center">
          <span className="grid h-14 w-14 place-items-center rounded-full bg-primary text-on-primary shadow-ambient">
            <span className="material-symbols-outlined text-[30px]">play_arrow</span>
          </span>
        </span>
        <WatermarkLayer />
      </button>
    );
  }

  return <YouTubePlayer videoId={videoId} autoplay overlay={<WatermarkLayer />} />;
}

const VideoBlock: Renderer = ({ block }) => {
  const c = block.content as { url?: string; title?: string; description?: string; caption?: string };
  if (!c.url) return null;
  // YouTube / Vimeo / direct URL detection
  const ytId = extractYouTubeId(c.url);
  const vmMatch = c.url.match(/vimeo\.com\/(\d+)/);
  // Ссылка Drive — страница, а не файл: в <video> она чёрная навсегда, играет только рамка предпросмотра (техдолг 242).
  const driveId = !ytId && !vmMatch ? driveFileId(c.url) : null;
  const frameSrc = vmMatch ? `https://player.vimeo.com/video/${vmMatch[1]}` : driveId ? drivePreviewUrl(driveId) : null;
  const player = ytId ? (
    isStoreBuild() ? (
      /* ⚠️ В ПРИЛОЖЕНИИ — НЕ ЧЕРЕЗ ВНУТРЕННИЙ АДРЕС.
         YouTube смотрит, с какого адреса открыт плеер, и внутренний адрес
         приложения отвергает: «Ошибка 153» вместо ролика (23.08.2026).
         EmbeddedPlayer рисует InlineYouTube: на iOS — нативный вид со страницей
         плеера `/embed/` на video.onebilim.kz (с 1.1.2, техдолг 401), иначе — окно
         на том же домене (lib/videoOutside.ts). Прежняя рамка `/embed/yt.html` с
         academy убрана; в первой её версии внутри урока запускалась вторая полная
         копия приложения, вебвью падало и просило заново Face ID и вход. */
      // Заголовок бывает с разметкой (D39) — в подпись плеера идёт только текст.
      <EmbeddedPlayer videoId={ytId} title={titleText(c.title) || undefined} />
    ) : (
      // Знак передаётся ВНУТРЬ плеера: он разворачивает свой контейнер, и всё,
      // что навешено снаружи, в полноэкранном режиме пропадает.
      <YouTubePlayer videoId={ytId} overlay={<WatermarkLayer />} />
    )
  ) : frameSrc ? (
    <iframe
      src={frameSrc}
      title={titleText(c.title) || 'video'}
      // `allowFullScreen` понимают не все браузеры; `allow` — нынешний способ.
      // Водяного знака у Vimeo и Drive нет, поэтому системный полный экран здесь можно.
      allow="fullscreen; picture-in-picture"
      allowFullScreen
      className="aspect-video w-full rounded-card"
    />
  ) : (
    <FileVideo url={c.url} />
  );
  // The block's title is rendered ONCE by the section wrapper (ex-head), same as
  // every other block — so VideoBlock must NOT render content.title again, or it
  // shows up twice (the visible "duplicate heading" bug). `title` живёт только в
  // `aria-label` плеера — там он и остаётся.
  // У ролика с YouTube и у видео файлом знак уже внутри разворачиваемого контейнера —
  // оборачивать второй раз не надо, иначе меток будет два слоя. Рамкам Vimeo и Drive обёртка нужна.
  return mediaFrame(c, ytId || !frameSrc ? player : <Watermark>{player}</Watermark>);
};

const AudioBlock: Renderer = ({ block }) => {
  const c = block.content as { url?: string; description?: string; caption?: string };
  if (!c.url) return null;
  // Общий плеер: загрузка, ошибка с «Повторить», перемотка, скорость — на
  // телефоне родной плеер загрузку не показывал никак.
  return mediaFrame(c, <AudioPlayer src={c.url} />);
};

const AudioRecordBlock: Renderer = ({ block, onAnswer, saved: savedAnswer, missing }) => {
  const { t } = useTranslation();
  const ask = useConfirm();
  const c = block.content as { instruction?: string; max_seconds?: number };
  /** Как в диктофоне: пишем, пауза, снова пишем (задача 6, 11.09.2026). */
  const [recState, setRecState] = useState<'idle' | 'recording' | 'paused'>('idle');
  const recording = recState !== 'idle';
  // Restore: answer_data holds the uploaded recording URL — play it back and
  // show the saved state so a refresh doesn't lose the submission.
  const [audioURL, setAudioURL] = useState<string | null>(savedAnswer?.answer_data ?? null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(!!savedAnswer);
  /** Запись принята, но лежит в очереди: связи нет, на сервер ещё не ушла. */
  const [queued, setQueued] = useState(false);
  /**
   * Что не так с записью, лежащей в очереди (02.10.2026): `stale` — не уходит больше недели, `failed` —
   * сервер отказал навсегда. Обе остаются на устройстве, и сказать об этом надо у самого задания.
   */
  const [queuedProblem, setQueuedProblem] = useState<'stale' | 'failed' | null>(null);
  /** Подсказка про «Запись без связи» — раз за задание, только в приложении при выключенном тумблере. */
  const [offlineHint, setOfflineHint] = useState(false);
  /** Имя записи (UUID) и момент «Стоп» — рождаются вместе с записью, договор `audio-upload.md` §1. */
  const clientIdRef = useRef<string | null>(null);
  const stoppedAtRef = useRef<number | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const blobRef = useRef<Blob | null>(null);
  const stopTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // ⚠️ Предел задания не короче минимума записи. `max_seconds` в конструкторе —
  // свободный ввод: при 3 секундах автостоп обрывал бы любую запись раньше 5, и
  // сдать такое задание было бы нельзя никогда (сервер отвергает запись короче 5 с).
  const max = Math.max(c.max_seconds ?? 180, MIN_RECORDING_SECONDS);

  // Часы записи. Раньше во время записи не было видно ничего: ни секунд, ни
  // полосы — человек не понимал, идёт запись или нет и сколько уже наговорил
  // (задача 121). Считаем по настоящему времени, а не по числу тиков: вкладка
  // в фоне усыпляет таймеры, и счётчик отставал бы от реальной длины записи.
  const [elapsed, setElapsed] = useState(0);
  /** Длительность готовой записи — то, что человек наговорил на самом деле. */
  const [duration, setDuration] = useState<number | null>(null);
  /** Готовая запись короче минимума (решение владельца 10.09.2026: не короче 5 секунд). */
  const [tooShort, setTooShort] = useState(false);
  /** Запись сделана в этом открытии урока — её длину знают свои часы. */
  const [fresh, setFresh] = useState(false);
  /** Длительность записи, пришедшей с сервера, — насчитал проигрыватель. */
  const [restoredSeconds, setRestoredSeconds] = useState<number | null>(null);
  /**
   * Длина, померенная СЕРВЕРОМ по звуку (12.09.2026, договор §2.3 п. 3).
   *
   * 🔴 ЗАЧЕМ. До сих пор под своей записью показывались часы страницы. Ученица
   * 11.09.2026 видела 2:30 у записи, в которой было 20 секунд, и отправила её,
   * ничего не заподозрив. Как только сервер подтвердил приём и назвал длину —
   * показываем её, а не то, что насчитали мы.
   */
  const serverRecording = useSyncExternalStore(
    subscribeRecordingResult,
    () => recordingResultOf(block.id),
    () => recordingResultOf(block.id),
  );
  /**
   * Что происходит с отправкой прямо сейчас.
   *
   * 🔴 ЗАЧЕМ. Ученик жал «Отправить» и видел только крутилку в кнопке. Сама
   * загрузка идёт в обработчике очереди, вне этого дерева, и на плохой связи
   * запись в несколько мегабайт уходит долго — молчание читается как
   * «платформа зависла» (lib/sendProgress.ts).
   */
  const sending = useSyncExternalStore(
    subscribeSendProgress,
    () => sendProgressOf(block.id),
    () => sendProgressOf(block.id),
  );
  // Часы считают только запись, без пауз — `lib/recordingClock.ts`. По стене 4 с
  // речи и 20 с паузы прошли бы минимум в 5 с, а сервер отверг бы запись.
  const clockRef = useRef(createRecordingClock());
  /**
   * Кто остановил запись (12.09.2026, договор `record-integrity.md` §2.1).
   *
   * ⚠️ `onstop` БЕЗ ОТМЕТКИ — ЭТО САМООСТАНОВКА. Разбор 12.09.2026 показал: когда
   * дорожка микрофона кончается, Chromium сам останавливает рекордер, и блок
   * молча переходит в «Отправить» — ученик отправляет обрывок, не поняв, что
   * произошло. Отличить это от нажатия кнопки можно только своей отметкой.
   */
  const stopFlagsRef = useRef<StopFlags>({});
  /** Причина последней остановки — уходит на сервер вместе с записью. */
  const stopReasonRef = useRef<StopReason | null>(null);
  /** Что насчитали часы у готовой записи, в миллисекундах. */
  const clientMsRef = useRef<number | null>(null);
  /** Сколько звука в файле по нашему замеру, в миллисекундах. */
  const fileMsRef = useRef<number | null>(null);
  /**
   * Микрофон пропал во время записи (12.09.2026).
   *
   * ⚠️ ПОКА ЗАПИСЬ ИДЁТ — это предупреждение «тебя не слышно», а ПОСЛЕ остановки
   * — красная плашка с причиной и временем. До сих пор блок не слушал ни одного
   * события дорожки: микрофон забирало другое приложение, часы шли дальше, и
   * ученик говорил в пустоту.
   */
  const [micLoss, setMicLoss] = useState<MicLoss | null>(null);
  /** Снять слежение за микрофоном. */
  const unwatchRef = useRef<(() => void) | null>(null);
  /**
   * Что не так с готовой записью — по её СОБСТВЕННОЙ длине (решение владельца §5.1).
   *
   * ⚠️ Замер идёт фоном и умеет только ДОБАВИТЬ запрет. Останавливаем мы
   * мгновенно: ученик не должен смотреть на замерший экран, пока браузер
   * разворачивает минуту звука в отсчёты.
   */
  const [verdict, setVerdict] = useState<RecordingVerdict | null>(null);
  /** Ученик переслушал оборванную запись и решил отправить как есть. */
  const [accepted, setAccepted] = useState(false);
  /** Номер записи: ответ фонового замера от прошлой записи к новой не относится. */
  const takeRef = useRef(0);
  /** Одна цепочка на все записи в черновик — чтобы куски не обгоняли друг друга. */
  const draftChainRef = useRef<Promise<void>>(Promise.resolve());
  /** Измеритель уровня, удержание экрана и полоска вверху — всё живёт ровно пока идёт запись. */
  const meterRef = useRef<MicMeter | null>(null);
  const wakeRef = useRef<(() => void) | null>(null);
  const barRef = useRef<(() => void) | null>(null);
  /**
   * Подсказка по уровню звука (14.09.2026): «Мы вас не слышим» — звука нет вовсе,
   * «Говорите громче» — звук слабый. Только рисуется, на запись не влияет.
   */
  const [hint, setHint] = useState<RecordHint>(null);
  const hintRunRef = useRef(createHintRun());
  /** Измеритель уровня открылся — есть что рисовать шкалой. */
  const [hasMeter, setHasMeter] = useState(false);
  const readLevel = useCallback(() => meterRef.current?.level() ?? 0, []);
  /** Ждём, вернётся ли замолчавший микрофон, прежде чем обрывать запись. */
  const muteWaitRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Запись собрана из черновика, а не записана только что. */
  const [fromDraft, setFromDraft] = useState(false);
  /** Черновик нашёлся, но файл в нём не получился: он стёрт, осталась одна строка (13.09.2026). */
  const [draftLost, setDraftLost] = useState(false);
  /**
   * 🔴 ИДЁТ ЗАПУСК ЗАПИСИ — ВТОРОЙ НЕ НАЧИНАТЬ (13.09.2026).
   *
   * Пока ждём микрофон, кнопка ещё «Записать», и второе нажатие заводило второй
   * рекордер. Останавливался только последний; первый жил до ухода со страницы и
   * каждые пять секунд подкладывал свой кусок без заголовка в следующую запись и
   * в черновик. На проде вечером 12.09: файл с двумя заголовками (133 с звука при
   * 67 с по часам) и 18 отказов «файл не получился»
   * (`docs/CHECK-2026-09-13-RECORD-STATS.md`). Ссылка — потому что два нажатия
   * приходят до перерисовки; состояние — чтобы кнопку было видно занятой.
   */
  const startingRef = useRef(false);
  const [starting, setStarting] = useState(false);
  const streamRef = useRef<MediaStream | null>(null);
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Паузы нет у старых браузеров — тогда нет и кнопки, запись идёт как раньше.
  //
  // ⚠️ И ЗА ФЛАГОМ `recordPause`, УМОЛЧАНИЕ — ВЫКЛЮЧЕНО (слово владельца 12.09.2026).
  // После появления паузы 11.09 доля обрывов записи на Mac-Safari и iPad выросла с
  // 3,6 до 18,3 % (docs/CHECK-2026-09-12-RECORD-TRUNC.md §2.4); кандидат — pause()/
  // resume() в WebKit, живьём не проверено. Вернуть — `features.recordPause` с сервера.
  const canPause =
    isFeatureOn('recordPause') && typeof MediaRecorder !== 'undefined' && typeof MediaRecorder.prototype.pause === 'function';
  /** Писать кусками и хранить черновик. Выключается с сервера, если платформа удивит. */
  const draftOn = isFeatureOn('recordChunks');

  /**
   * Погасить всё, что живёт только во время записи.
   *
   * ⚠️ ОДНО МЕСТО НА ВСЕ ВЫХОДЫ. Остановка, конец записи и уход со страницы —
   * три разные дороги, и забытый на одной из них измеритель оставил бы открытым
   * звуковой контекст, а забытая полоска висела бы поверх урока без записи.
   */
  const clearMuteWait = () => {
    if (muteWaitRef.current) {
      clearTimeout(muteWaitRef.current);
      muteWaitRef.current = null;
    }
  };

  const stopCompanions = () => {
    clearMuteWait();
    meterRef.current?.stop();
    meterRef.current = null;
    wakeRef.current?.();
    wakeRef.current = null;
    barRef.current?.();
    barRef.current = null;
    setHint(null);
    setHasMeter(false);
  };

  const stopTicking = () => {
    if (tickRef.current) {
      clearInterval(tickRef.current);
      tickRef.current = null;
    }
  };
  const startTicking = () => {
    stopTicking();
    // После паузы тишина считается заново: пауза — не «мы вас не слышим».
    hintRunRef.current = createHintRun();
    setHint(null);
    tickRef.current = setInterval(() => {
      const now = Date.now();
      setElapsed(Math.min(max, clockRef.current.elapsedMs(now) / 1000));
      // Уровень спрашиваем здесь же: отдельный таймер ради того же счёта был бы
      // вторым источником правды о том, идёт ли запись.
      const meter = meterRef.current;
      if (meter) {
        const level = meter.level();
        // ⚠️ Спящий звуковой контекст отдаёт нули — это не ноль микрофона: подсказки молчат.
        setHint(meter.live() ? hintRunRef.current.push(level, now, meter.precise) : null);
      }
    }, 200);
  };
  const clearAutoStop = () => {
    if (stopTimerRef.current) {
      clearTimeout(stopTimerRef.current);
      stopTimerRef.current = null;
    }
  };

  /**
   * Уход со страницы во время записи.
   *
   * ⚠️ РАНЬШЕ ГАСИЛИСЬ ТОЛЬКО ЧАСЫ: рекордер, микрофон и автостоп продолжали жить,
   * и у ученика горел значок микрофона на уже закрытом уроке (найдено 11.09.2026).
   * Обработчики снимаются ДО остановки — иначе она обновила бы снятый с экрана блок.
   */
  const releaseRecorder = () => {
    stopTicking();
    clearAutoStop();
    unwatchRef.current?.();
    unwatchRef.current = null;
    stopCompanions();
    // Уход со страницы — тоже причина, и она честнее, чем «остановилась сама».
    stopFlagsRef.current.leave = true;
    const rec = recorderRef.current;
    if (rec) {
      rec.ondataavailable = null;
      rec.onstop = null;
      if (rec.state !== 'inactive') rec.stop();
    }
    streamRef.current?.getTracks().forEach((tr) => tr.stop());
    streamRef.current = null;
  };
  // Только при снятии блока с экрана: функция работает с ссылками, свежесть
  // замыкания ей не нужна, а зависимость пересоздавала бы уборку на каждой отрисовке.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => releaseRecorder, []);

  /**
   * Незаконченная запись, уцелевшая после перезагрузки страницы.
   *
   * 🔴 ЗАЧЕМ. Ученик говорит две минуты, вкладка перезагружается — на телефоне
   * это делает и сама система, когда ей мало памяти, — и работа пропадает вся.
   * Куски лежат в хранилище браузера, и из них собирается та же запись.
   *
   * ⚠️ ОТВЕТ С СЕРВЕРА ВАЖНЕЕ ЧЕРНОВИКА: если ответ уже сохранён, предлагать
   * «восстановить» значило бы звать отправить то же самое второй раз.
   */
  /**
   * Стереть черновик В ТОЙ ЖЕ ЦЕПОЧКЕ, что пишет куски (13.09.2026).
   *
   * ⚠️ Мимо цепочки стирание обгоняло последний кусок, записанный уже после «Стоп»:
   * кусок ложился в пустое место и становился первым в следующем черновике — файл
   * без начала. Поймано проверкой `recordDoubleStart.test.ts`.
   */
  const dropDraftQueued = () => {
    draftChainRef.current = draftChainRef.current.then(() => dropDraft(block.id)).catch(() => undefined);
  };

  /**
   * ⚠️ ЧЕРНОВИК ПОКАЗЫВАЕТСЯ ТОЛЬКО ПОСЛЕ ПРОВЕРКИ (13.09.2026). Живой случай: iPhone,
   * 1:43, перезагрузка — и блок предложил черновик без начала файла: плеер «не
   * загрузилась», «длину проверить не удалось — отправить можно» и кнопка. Сервер
   * шесть раз подряд ответил «файл не получился». Теперь: начало опознано, длина
   * читается или файл хотя бы играет. Иначе черновик стирается, и остаётся одна
   * строка без плеера и отправки. Пока идёт проверка — не показываем ничего.
   */
  useEffect(() => {
    // Задание с записью встретилось на этом устройстве — тумблеру «Запись без связи» есть что обещать.
    markVoiceTaskSeen();
    let alive = true;
    // Ученик успел начать новую запись — ответ проверки к ней не относится.
    const take = takeRef.current;
    const current = () => alive && takeRef.current === take;
    void (async () => {
      // 🔴 СНАЧАЛА ОЧЕРЕДЬ (02.10.2026, дыра 4 сверки). Запись отправлена, но ещё лежит на устройстве: ответа с
      // сервера нет, черновик стёрт — блок был пустым, «Записать ответ», и ученик записывал заново, решив,
      // что работа пропала. Теперь задание показывает ту же запись и её состояние, что и сразу после
      // «Отправить». Запись в очереди важнее и ответа с сервера, и черновика: она новее обоих.
      const item = await queuedItem(lessonAudioId(block.id));
      if (!current()) return;
      if (item) {
        const payload = item.payload as LessonAudioPayload;
        const bytes = await loadLessonAudio(payload).catch(() => null);
        if (!current()) return;
        if (bytes) {
          const blob = new Blob([bytes], { type: payload.mime || 'audio/webm' });
          blobRef.current = blob;
          setAudioURL(URL.createObjectURL(blob));
        }
        clientIdRef.current = payload.clientId ?? null;
        stoppedAtRef.current = payload.stoppedAt ?? null;
        setDuration(typeof payload.clientSeconds === 'number' ? Math.round(payload.clientSeconds) : null);
        setFresh(true);
        setSaved(true);
        setQueued(true);
        setQueuedProblem(item.permanentFail ? 'failed' : isOutboxItemStale(item) ? 'stale' : null);
        return;
      }
      // Отказ, пришедший, пока задания не было на экране: запись снята с очереди, ученику — причина.
      const noticed = takeRecordNotice(block.id);
      if (noticed) {
        const rejection = openAnswerRejection({ raw: { code: noticed } });
        setError(rejection ? t(rejection.key, rejection.params) : t('blockRenderers.audioSaveError'));
      }
      if (!draftOn || savedAnswer?.answer_data) return;
      const draft = await readDraft(block.id);
      if (!current() || !draft || !draft.parts.length) return;
      const lost = (reason: BrokenRecordingReason) => {
        dropDraftQueued();
        setDraftLost(true);
        // В серверный журнал `[запись]` — с устройством и причиной (решение владельца 13.09.2026).
        reportBrokenRecording({ blockId: block.id, reason, clientSeconds: draft.elapsedMs / 1000 });
      };

      // Тип — по байтам: черновик подписывал mp4 с iPhone как webm.
      const raw = new Blob(draft.parts, { type: draft.mime });
      const sniffed = await sniffBlob(raw);
      if (!current()) return;
      if (!sniffed) return lost('client_nohead');
      const blob = draft.mime.split(';')[0].trim() === sniffed ? raw : new Blob(draft.parts, { type: sniffed });

      const clientSeconds = draft.elapsedMs / 1000;
      const measured = canMeasureAudio();
      const fileSeconds = await measureBlobSeconds(blob);
      if (!current()) return;
      const url = URL.createObjectURL(blob);
      // Играет ли — спрашиваем, только если длина не прочлась: прочлась — значит, звук есть.
      const playable = fileSeconds == null ? await probePlayable(url) : undefined;
      if (!current()) {
        URL.revokeObjectURL(url);
        return;
      }
      const checked = recordingVerdict({ clientSeconds, fileSeconds, bytes: blob.size, measured, playable, recognized: true });
      if (checked.kind === 'broken' || checked.kind === 'empty') {
        URL.revokeObjectURL(url);
        return lost('client_noplay');
      }

      blobRef.current = blob;
      clientMsRef.current = draft.elapsedMs;
      fileMsRef.current = fileSeconds == null ? null : Math.round(fileSeconds * 1000);
      // Чем запись кончилась на самом деле, мы не знаем: выдавать это за нажатие
      // кнопки нельзя, поэтому причина — «перезагрузка».
      stopReasonRef.current = 'reload';
      // Имя у восстановленной записи новое, а момент остановки — когда лёг последний кусок черновика.
      clientIdRef.current = newClientId();
      stoppedAtRef.current = draft.updatedAt;
      setAudioURL(url);
      setDuration(Math.round(draft.elapsedMs / 1000));
      setTooShort(recordingTooShort(draft.elapsedMs));
      setFresh(true);
      setFromDraft(true);
      setVerdict(checked);
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [block.id]);

  /**
   * Запись ушла из очереди, пока задание на экране (02.10.2026).
   *
   * ⚠️ РАНЬШЕ «ЖДЁТ ОТПРАВКИ» ОСТАВАЛОСЬ НАВСЕГДА: `queued` ставился один раз по исходу «Отправить», и когда
   * очередь позже доставляла запись сама, блок об этом не узнавал. Теперь: дошла — «Отправлено»; сервер
   * отказал (работу уже приняли, файла больше нет) — причина у задания, а не немая пропажа.
   */
  useEffect(
    () =>
      onOutboxSettled((item, how, err) => {
        if (item.id !== lessonAudioId(block.id)) return;
        if (how === 'delivered') {
          // И после отказа хранилища: запись жила в памяти страницы и всё же дошла.
          setSaved(true);
          setQueued(false);
          setQueuedProblem(null);
          setError(null);
          return;
        }
        if (how !== 'rejected') return;
        takeRecordNotice(block.id); // сказано здесь же — заметка на потом не нужна
        const rejection = openAnswerRejection(err);
        setError(rejection ? t(rejection.key, rejection.params) : t('blockRenderers.audioSaveError'));
        setSaved(false);
        setQueued(false);
        setQueuedProblem(null);
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [block.id],
  );

  /** «Отправить сейчас» у записи, лежащей в очереди: тот же проход, что у кнопки в профиле. */
  const sendQueuedNow = () => {
    void flushOutbox();
  };

  /** Стереть запись из очереди — только сам ученик, с подтверждением (решение владельца 02.10.2026). */
  const deleteQueued = async () => {
    if (!(await ask({ message: t('blockRenderers.audioQueuedDeleteConfirm'), confirmLabel: t('common.delete'), danger: true }))) {
      return;
    }
    await removeQueued(lessonAudioId(block.id));
    takeRef.current += 1;
    blobRef.current = null;
    clientIdRef.current = null;
    stoppedAtRef.current = null;
    setAudioURL(savedAnswer?.answer_data ?? null);
    setSaved(!!savedAnswer);
    setQueued(false);
    setQueuedProblem(null);
    setDuration(null);
    setFresh(false);
    setError(null);
  };

  // Пока идёт запись или пауза, страница урока предупреждает об уходе
  // (`hooks/useLeaveWhileRecording.ts`).
  const busy = recState !== 'idle';
  useEffect(() => (busy ? recordingStarted() : undefined), [busy]);

  // Сохранение = отдать саму запись наверх, а не ссылку на неё.
  //
  // БЫЛО ДВА РАЗНЫХ ДЕФЕКТА, ОБА ИСПРАВЛЕНЫ.
  // 1. Сначала блок сообщал локальную ссылку `blob:`, которая живёт только в
  //    этой вкладке — сервер и куратор получали мёртвую ссылку.
  // 2. Потом запись стала грузиться сразу, отдельным запросом, мимо очереди
  //    неотправленного. Оборвалась связь — запись пропадала совсем: ушёл со
  //    страницы, и её больше нет. Кнопка «Синхронизировать» о голосе не знала.
  // Теперь наверх уходит сам файл: очередь сама загрузит его и сама отметит
  // ответ, когда связь появится (задача 120).
  const saveRecording = async () => {
    if (!blobRef.current) return;
    setSaving(true);
    setError(null);
    try {
      const delivered = await onAnswer?.({
        answer_data: '',
        is_correct: true,
        blob: blobRef.current,
        // ⚠️ Часы и причину остановки шлём ВСЕГДА, когда они известны: по ним
        // сервер сверяет звук в файле с тем, что видел ученик на экране. Без
        // них он отвечает прежним кодом «короче 5 секунд» на любую беду —
        // и две минуты речи в отключённый микрофон получают совет записать
        // подлиннее (договор `record-integrity.md` §2.2).
        // 🔴 СОГЛАСИЛСЯ — ЗНАЧИТ ЧАСЫ НЕ ШЛЁМ. Сервер считает обрыв по расхождению
        // «часы против звука» и про согласие ученика не знает: он отверг бы
        // запись снова, и «Переслушать и решить» вело бы в круг без выхода.
        // Ровно в такой круг 12.09.2026 попал ученик b54ea6aa — 14 отказов
        // подряд на одном задании и пустой ответ в итоге. Причину остановки
        // шлём по-прежнему, поэтому для сервера мы остаёмся новым клиентом и
        // прочие его отказы разбираем как раньше.
        clientSeconds:
          accepted || clientMsRef.current == null
            ? undefined
            : Math.round(clientMsRef.current / 100) / 10,
        stopReason: stopReasonRef.current ?? undefined,
        clientId: clientIdRef.current ?? undefined,
        stoppedAt: stoppedAtRef.current ?? undefined,
      });
      // 🔴 ЧЕРНОВИК СТИРАЕМ ТОЛЬКО ПОСЛЕ ТОГО, КАК ЗАПИСЬ ЛЕГЛА В ОЧЕРЕДЬ (02.10.2026, дыра 1 сверки). Раньше он
      // стирался первой строкой: хранилище отказывало очереди — и от записи не оставалось ничего, а на
      // экране стояло «сохранена на устройстве». Запись в очереди — черновик больше не нужен: предложить его
      // позже значило бы предложить отправить то же самое во второй раз.
      dropDraftQueued();
      setSaved(true);
      setQueued(delivered === false);
      setQueuedProblem(null);
      // Не дошло с первого раза, а «Запись без связи» выключена — одна строка со ссылкой на тумблер.
      if (delivered === false && shouldHintOfflineRecords(block.id)) {
        markOfflineHintShown(block.id);
        setOfflineHint(true);
      }
    } catch (e) {
      // 🔴 ХРАНИЛИЩЕ ОТКАЗАЛО — ЭТО ОШИБКА, А НЕ «СОХРАНЕНА» (02.10.2026). Запись осталась в памяти страницы и в
      // черновике (его мы не стёрли) и уйдёт, пока страница открыта; на устройстве её нет, и говорить
      // обратное нельзя. Кнопка «Отправить» остаётся — повторное нажатие попробует ещё раз.
      if (isOutboxStorageError(e)) {
        setError(t('blockRenderers.audioStoreError'));
        return;
      }
      // Отказ сервера по самой записи — черновик того же файла предлагать незачем.
      dropDraftQueued();
      // Отказ сервера — своими словами на каждую причину; прочее — как раньше.
      const rejection = openAnswerRejection(e);
      setError(rejection ? t(rejection.key, rejection.params) : t('blockRenderers.audioSaveError'));
      // ⚠️ СЕРВЕР СКАЗАЛ «ОБОРВАЛАСЬ» — ДАЛЬШЕ РЕШАЕТ УЧЕНИК. Он уже прочитал,
      // сколько на самом деле сохранилось; если нажмёт «Отправить» ещё раз, это
      // осознанное решение, и часы мы больше не пошлём. Без этого повторное
      // нажатие даёт тот же отказ бесконечно — так и вышло у ученика b54ea6aa.
      const code = apiErrorCode(e);
      if (code === 'recording_truncated') setAccepted(true);
      // 🔴 СЕРВЕР СКАЗАЛ «ФАЙЛ НЕ ПОЛУЧИЛСЯ» — ОТПРАВЛЯТЬ ЭТОТ ЖЕ ФАЙЛ СНОВА НЕЛЬЗЯ
      // (13.09.2026). Вечером 12.09 ученик отправил один и тот же битый файл шесть
      // раз подряд: кнопка оставалась. Текст причины уже стоит в `error`, поэтому у
      // вердикта своих слов нет. «Файла нет на сервере» (`no_file`) сюда не входит:
      // там сама запись у ученика может быть целой.
      const reason = apiErrorReason(e);
      if (code === 'file_empty' || code === 'file_unreadable' || (code === 'empty_recording' && reason === 'unreadable')) {
        setAccepted(false);
        setVerdict({ kind: 'broken', canSave: false });
      }
    } finally {
      setSaving(false);
    }
  };

  /**
   * Остановка. `by` — чем именно: кнопкой или автостопом на пределе задания.
   *
   * ⚠️ Отметку ставим ДО `rec.stop()`: `onstop` может прийти синхронно, и тогда
   * причина вычислялась бы по ещё пустым флагам — то есть «сама остановилась».
   */
  const stopRecording = (by: 'button' | 'limit' | 'loss' = 'button') => {
    clearAutoStop();
    stopTicking();
    unwatchRef.current?.();
    unwatchRef.current = null;
    // Потеря микрофона отмечена там, где случилась: здесь отмечать нечего,
    // иначе кнопка перебила бы настоящую причину.
    if (by === 'loss') { /* отметка уже стоит */ }
    else if (by === 'limit') stopFlagsRef.current.limit = true;
    else {
      stopFlagsRef.current.button = true;
      // Кнопка, нажатая с паузы, — отдельная причина: случайная пауза посреди
      // ответа иначе неотличима от честной остановки.
      stopFlagsRef.current.paused = recState === 'paused';
    }
    const rec = recorderRef.current;
    // Остановить можно и с паузы — запись закончится тем, что уже наговорено.
    if (rec && rec.state !== 'inactive') rec.stop();
  };

  /** Автостоп на пределе задания — по записанному времени, пауза его не съедает. */
  const scheduleAutoStop = () => {
    clearAutoStop();
    stopTimerRef.current = setTimeout(() => stopRecording('limit'), clockRef.current.remainingMs(max * 1000, Date.now()));
  };

  const pauseRecording = () => {
    const rec = recorderRef.current;
    if (!rec || rec.state !== 'recording') return;
    rec.pause();
    clockRef.current.pause(Date.now());
    clearAutoStop();
    stopTicking();
    setElapsed(Math.min(max, clockRef.current.elapsedMs(Date.now()) / 1000));
    setRecState('paused');
  };

  const resumeRecording = () => {
    const rec = recorderRef.current;
    if (!rec || rec.state !== 'paused') return;
    rec.resume();
    clockRef.current.resume(Date.now());
    startTicking();
    scheduleAutoStop();
    setRecState('recording');
  };

  const startRecording = async () => {
    // 🔴 Запуск уже идёт — второй рекордер был бы сиротой (см. `startingRef`).
    if (startingRef.current) return;
    setError(null);
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      setError(t('blockRenderers.audioNoSupport'));
      return;
    }
    startingRef.current = true;
    setStarting(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // ⚠️ Прежний рекордер почему-то ещё пишет — гасим его и снимаем обработчики,
      // иначе его куски попадут в новую запись.
      const stale = recorderRef.current;
      if (stale && stale.state !== 'inactive') {
        stale.ondataavailable = null;
        stale.onstop = null;
        try {
          stale.stop();
        } catch {
          /* уже остановлен */
        }
      }
      if (streamRef.current && streamRef.current !== stream) streamRef.current.getTracks().forEach((tr) => tr.stop());
      streamRef.current = stream;
      // ⚠️ Битрейт — число с сервера, в сборке ноль: «как решит браузер», то есть как было всегда
      // (`lib/offlineRecords.ts`, `recordBitrate`). Рекордер, который такой параметр не принял, создаём
      // без него: запись важнее размера файла.
      const bitrate = recordBitrate();
      let rec: MediaRecorder;
      try {
        rec = bitrate > 0 ? new MediaRecorder(stream, { audioBitsPerSecond: bitrate }) : new MediaRecorder(stream);
      } catch {
        rec = new MediaRecorder(stream);
      }
      chunksRef.current = [];
      // Пауза — это пауза ОДНОГО рекордера, а не новая запись: поток один, и куски
      // складываются в цельный файл по порядку. Несколько отдельных записей
      // склеить так нельзя — у каждой свой заголовок.
      rec.ondataavailable = (e) => {
        // 🔴 КУСОК НЕ СВОЕГО РЕКОРДЕРА ОТБРАСЫВАЕМ (13.09.2026): именно так в файл
        // попадало чужое начало без заголовка.
        if (recorderRef.current !== rec) return;
        if (e.data.size <= 0) return;
        chunksRef.current.push(e.data);
        // ⚠️ Кусок уходит в хранилище браузера сразу. Это единственное, что
        // остаётся от записи, если вкладку перезагрузят посреди неё, — а на
        // телефоне её перезагружает и сама система, когда ей мало памяти.
        if (!draftOn) return;
        const at = clockRef.current.elapsedMs(Date.now());
        // 🔴 КУСКИ ПИШУТСЯ СТРОГО ПО ОЧЕРЕДИ, ОДНОЙ ЦЕПОЧКОЙ.
        //
        // Отдельная цепочка на каждый кусок ломает черновик двумя способами
        // сразу, и оба молча: первый кусок может уйти РАНЬШЕ, чем черновик
        // заведён, — а без него файл нечитаем целиком, у него там заголовок; и
        // два куска, начатые вместе, прочитают один и тот же номер и запишутся
        // на одно место. Найдено своей же проверкой 12.09.2026.
        draftChainRef.current = draftChainRef.current
          .then(() => e.data.arrayBuffer())
          .then((buf) => appendChunk(block.id, buf, at))
          .then(() => undefined)
          .catch(() => undefined);
      };
      rec.onstop = () => {
        // Остановка чужого рекордера не должна перезаписывать экран своей записи.
        if (recorderRef.current !== rec) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        stream.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
        stopTicking();
        stopCompanions();
        clockRef.current.stop(Date.now());
        // ⚠️ ТИП БЕРЁМ И У КУСКОВ ТОЖЕ. Safari на iPhone `mimeType` рекордера
        // отдаёт не всегда, а пишет он mp4, а не webm: подписав файл «webm», мы
        // отправили бы его с чужим расширением — очередь берёт его как раз из
        // типа. Байты сервер разбирает сам, но врать в имени незачем.
        const chunkType = chunksRef.current.find((part) => part.type)?.type;
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || chunkType || 'audio/webm' });
        blobRef.current = blob; // keep the real blob so Save can upload it
        // Имя записи рождается в момент «Стоп» и живёт с ней до сервера: перезаписал — новое имя.
        clientIdRef.current = newClientId();
        stoppedAtRef.current = Date.now();
        const url = URL.createObjectURL(blob);
        setAudioURL(url);
        // Длительность берём по своим часам, а не у проигрывателя: у записи
        // с микрофона в заголовке файла длины нет, и браузер отдаёт
        // «бесконечность». Часы — без пауз: это то, что человек наговорил.
        const ms = clockRef.current.elapsedMs(Date.now());
        clientMsRef.current = ms;
        // Причина остановки — по отметкам, поставленным ДО `rec.stop()`.
        // Отметок нет вовсе — значит рекордер остановился сам.
        stopReasonRef.current = pickStopReason(stopFlagsRef.current);
        setDuration(Math.round(ms / 1000));
        // Минимум — по миллисекундам и по сумме кусков: 4,5 с округлились бы до 5
        // и прошли клиент, а сервер их отверг бы.
        setTooShort(recordingTooShort(ms));
        setFresh(true);
        setSaved(false);
        setQueued(false);
        setQueuedProblem(null);
        setOfflineHint(false);
        setRecState('idle');
        // ⚠️ СНАЧАЛА мгновенный вердикт по тому, что известно сразу: пустой файл
        // виден по размеру и ловится здесь же, без загрузки и без ожидания.
        // Остальное пока «можно отправлять» — запрет добавит замер, если найдёт
        // за что.
        const clientSeconds = ms / 1000;
        setVerdict(recordingVerdict({ clientSeconds, fileSeconds: null, bytes: blob.size, measured: false }));
        setAccepted(false);
        const take = takeRef.current;
        const measured = canMeasureAudio();
        // ⚠️ Декод файла занимает заметное время на длинной записи и слабом
        // телефоне, а на экране в этот момент не происходило ничего: кнопка
        // «Отправить» уже есть, но вердикт ещё не вынесен.
        if (measured) publishSendProgress(block.id, { phase: 'checking', percent: null });
        void measureBlobSeconds(blob).then(async (fileSeconds) => {
          // Ученик успел перезаписать — ответ про прошлую запись выбрасываем.
          if (takeRef.current !== take) {
            publishSendProgress(block.id, null);
            return;
          }
          fileMsRef.current = fileSeconds == null ? null : Math.round(fileSeconds * 1000);
          if (fileSeconds == null) {
            // 🔴 ДЛИНА НЕ ПРОЧЛАСЬ — СВЕЖУЮ ЗАПИСЬ СУДИМ ТОЛЬКО ПО БАЙТАМ (решение владельца
            // 13.09.2026). Начало файла не опознано — «не получилось», отправки нет. Начало
            // в порядке — «не смогли проверить, можно отправить», даже если плеер не играет.
            //
            // ⚠️ ПЛЕЕРОМ СВЕЖУЮ ЗАПИСЬ НЕ ПРОВЕРЯЕМ. Живого iPhone в проверке не было, а
            // Safari без касания может не прочитать даже метаданные: проверка плеером
            // закрыла бы отправку честной записи. Плеером проверяется только черновик
            // после перезагрузки — там риск другой, и файл у нас уже не из этой сессии.
            const sniffed = await sniffBlob(blob);
            if (takeRef.current !== take) {
              publishSendProgress(block.id, null);
              return;
            }
            publishSendProgress(block.id, null);
            const recognized = sniffed != null;
            setVerdict(recordingVerdict({ clientSeconds, fileSeconds, bytes: blob.size, measured, recognized }));
            if (!recognized) reportBrokenRecording({ blockId: block.id, reason: 'client_nohead', clientSeconds });
            return;
          }
          publishSendProgress(block.id, null);
          setVerdict(recordingVerdict({ clientSeconds, fileSeconds, bytes: blob.size, measured }));
        });
      };
      recorderRef.current = rec;
      // Отметки предыдущей остановки к новой записи не относятся; замер сервера
      // за прошлую запись — тем более.
      stopFlagsRef.current = {};
      setMicLoss(null);
      stopReasonRef.current = null;
      clientMsRef.current = null;
      fileMsRef.current = null;
      takeRef.current += 1;
      setVerdict(null);
      setAccepted(false);
      setFromDraft(false);
      setDraftLost(false);
      forgetRecordingResult(block.id);
      // ⚠️ СЛЕЖЕНИЕ ЗА МИКРОФОНОМ (12.09.2026). До сих пор блок не слушал ни
      // одного события дорожки, и потеря микрофона выглядела на экране как
      // обычная запись: часы шли, ученик говорил, в файл не попадало ничего.
      unwatchRef.current = watchMicrophone({
        // ⚠️ Не у всякого потока есть `getAudioTracks`: у части старых браузеров
        // и у поддельных потоков в проверках только `getTracks`. Падать здесь
        // нельзя — иначе вся запись обрывается отказом «нет доступа к микрофону».
        tracks: stream.getAudioTracks?.() ?? stream.getTracks?.() ?? [],
        recorder: rec,
        devices: navigator.mediaDevices,
        elapsedMs: () => clockRef.current.elapsedMs(Date.now()),
        onLoss: (loss) => {
          setMicLoss(loss);
          stopFlagsRef.current[loss.kind] = true;
          if (loss.kind !== 'mute') {
            // Дорожки больше нет или упал рекордер — записывать нечем.
            stopRecording('loss');
            return;
          }
          // 🔴 «ЗАМОЛЧАЛ» БЫВАЕТ ДВУХ РОДОВ, И РАЗЛИЧАЕТ ИХ ТОЛЬКО ВРЕМЯ.
          // Короткий — наушники дёрнулись, пришло уведомление: звук вернётся, и
          // обрывать запись значило бы отнять работу на ровном месте.
          // Длинный — ученик ушёл в другое приложение. ЗАМЕРЕНО НА ЖИВОМ iPhone
          // (iOS 18.7, Safari 26.6.1, 12.09.2026): при уходе в фон приходит
          // `mute`, и дальше в файл не попадает НИЧЕГО — даже после возвращения.
          // Из 30 секунд по часам в файле осталось 8. Не останови мы запись,
          // ученик говорил бы в мёртвый рекордер сколько угодно долго.
          clearMuteWait();
          muteWaitRef.current = setTimeout(() => stopRecording('loss'), MUTE_GRACE_MS);
        },
        onBack: () => {
          clearMuteWait();
          setMicLoss(null);
        },
      });
      // Спутники записи: шкала уровня, удержание экрана и полоска вверху.
      // Каждый — необязательный: нет WebAudio или замка, запись идёт как раньше.
      meterRef.current = attachMicLevel(stream);
      setHasMeter(!!meterRef.current);
      wakeRef.current = keepScreenAwake();
      barRef.current = startBar({
        blockId: block.id,
        elapsedMs: () => clockRef.current.elapsedMs(Date.now()),
        level: () => meterRef.current?.level() ?? 0,
        state: () => (clockRef.current.state === 'paused' ? 'paused' : 'recording'),
        stop: () => stopRecording('button'),
        // Ученик начал записывать другое задание — эту запись доводим до конца
        // сами, а не бросаем рекордер и микрофон включёнными.
        onPreempt: () => stopRecording('button'),
      });
      // Кусками — чтобы было что восстанавливать. Замерено живьём (Safari 26.5.2,
      // Chrome 151, iPhone iOS 18.7): длина файла от этого не теряется.
      if (draftOn) {
        // Черновик заводится первым звеном той же цепочки — куски встанут за ним.
        // ⚠️ ДОПИСЫВАЕМ К ЦЕПОЧКЕ, А НЕ ЗАМЕНЯЕМ ЕЁ (13.09.2026): последний кусок прошлой
        // записи ещё пишется, и стирание мимо очереди ставило его первым в новый черновик.
        // Тип спрашиваем уже после `start()` — до него Safari его не называет.
        draftChainRef.current = draftChainRef.current
          .then(() => openDraft(block.id, rec.mimeType || 'audio/webm', 0))
          .then(() => undefined)
          .catch(() => undefined);
        rec.start(DRAFT_CHUNK_MS);
      } else {
        rec.start();
      }
      clockRef.current.start(Date.now());
      setRecState('recording');
      setDuration(null);
      setTooShort(false);
      setElapsed(0);
      startTicking();
      scheduleAutoStop();
    } catch {
      setError(t('blockRenderers.audioMicError'));
    } finally {
      startingRef.current = false;
      setStarting(false);
    }
  };

  /** Отправку закрыл вердикт, и ученик ещё не решил иначе. */
  const sendBlocked = !!verdict && !verdict.canSave && !accepted;
  /**
   * Настоять на своём можно только у оборванной записи.
   *
   * ⚠️ У ПУСТОГО ФАЙЛА КНОПКИ «переслушать» НЕТ: слушать там нечего, и отправка
   * такой записи кончится отказом сервера — то есть той же стеной, только позже.
   */
  const canOverride = verdict?.kind === 'truncated';

  /**
   * Корзина: стереть запись из браузера (решение владельца 12.09.2026).
   *
   * ⚠️ ТОЛЬКО МЕСТНУЮ, ЕЩЁ НЕ ОТПРАВЛЕННУЮ. Ответ, уже принятый сервером, этой
   * кнопкой не стирается — ручки удаления ответа нет; там остаётся «Записать заново».
   */
  const discardRecording = async () => {
    if (!(await ask({ message: t('blockRenderers.recordDiscardConfirm'), confirmLabel: t('common.delete'), danger: true }))) {
      return;
    }
    takeRef.current += 1;
    dropDraftQueued();
    blobRef.current = null;
    clientMsRef.current = null;
    fileMsRef.current = null;
    stopReasonRef.current = null;
    setAudioURL(null);
    setDuration(null);
    setTooShort(false);
    setVerdict(null);
    setAccepted(false);
    setFromDraft(false);
    setMicLoss(null);
    setError(null);
    setFresh(false);
  };

  // ⚠️ СОХРАНЁННАЯ ЗАПИСЬ КОРОЧЕ 5 СЕКУНД (11.09.2026, 50 учеников на проде). Запись,
  // сохранённая до правила, приходила с сервера и показывалась зелёным «Сохранено»,
  // а раздел не закрывался. Судим по длительности у проигрывателя или по отказу 409.
  const savedProblem =
    saved && !fresh && !recording
      ? savedOpenAnswerProblem({
          type: 'audio_record',
          answer: audioURL ?? '',
          durationSec: restoredSeconds,
          namedByServer: missing,
        })
      : null;

  return (
    <div className="space-y-3">
      <RichText text={c.instruction} className="text-on-surface" />
      <div className="flex flex-wrap items-center gap-3">
        <Button
          variant={recording ? 'danger' : 'primary'}
          // Пока ждём микрофон, кнопка занята: второе нажатие заводило второй рекордер.
          loading={starting}
          onClick={() => (recording ? stopRecording() : startRecording())}
        >
          <span className="material-symbols-outlined">{recording ? 'stop' : 'mic'}</span>
          {recording
            ? t('blockRenderers.audioStop')
            : // ⚠️ Запись уже есть — значит кнопка начинает ЗАНОВО, и называться
              // должна так же. «Записать ответ» над готовой записью выглядит
              // так, будто её нет, и ученик жмёт, не понимая, что потеряет.
              // Запись в очереди — тоже «есть»: над ней только явное «Записать заново».
              savedProblem || (audioURL && !saved) || queued
              ? t('blockRenderers.audioReRecord')
              : t('blockRenderers.audioRecord')}
        </Button>
        {/* Пауза как в диктофоне: «Пауза» → «Продолжить». На паузе часы стоят. */}
        {recording && canPause && (
          <Button
            variant="secondary"
            onClick={() => (recState === 'paused' ? resumeRecording() : pauseRecording())}
          >
            <span className="material-symbols-outlined">{recState === 'paused' ? 'mic' : 'pause'}</span>
            {recState === 'paused' ? t('blockRenderers.audioResume') : t('blockRenderers.audioPause')}
          </Button>
        )}
        {recording ? (
          // Во время записи показываем настоящие секунды и полосу до предела:
          // человек должен видеть, что его слышат, и сколько осталось.
          <span
            className="flex min-w-[10rem] flex-1 items-center gap-3 text-sm tabular-nums text-on-surface"
            role="timer"
            aria-live="off"
          >
            <span className="font-semibold">{clock(elapsed)}</span>
            {recState === 'paused' && (
              <span className="text-on-surface-variant">{t('blockRenderers.audioPaused')}</span>
            )}
            <span className="h-1.5 min-w-16 flex-1 overflow-hidden rounded-pill bg-surface-container-highest">
              <span
                className="block h-full rounded-pill bg-error transition-[width] duration-200"
                style={{ width: `${Math.min(100, (elapsed / max) * 100)}%` }}
              />
            </span>
            <span className="text-on-surface-variant">{clock(max)}</span>
          </span>
        ) : (
          <span className="text-sm text-on-surface-variant">{t('blockRenderers.audioMax', { max })}</span>
        )}
      </div>
      {/* «Готовим микрофон» → «Говорите», подсказка и крупная шкала (14.09.2026).
          «Говорите» — только когда рекордер уже запущен и часы пошли. */}
      {(recording || starting) && (
        <RecordLevelPanel
          phase={recState === 'recording' ? 'recording' : recState === 'paused' ? 'paused' : 'preparing'}
          hint={micLoss ? null : hint}
          level={hasMeter ? readLevel : null}
        />
      )}
      {error && <p className="text-sm text-error">{error}</p>}
      {/* ⚠️ Микрофон пропал. Пока запись идёт — предупреждение «тебя не слышно»
          (оно живое и снимается, если микрофон вернулся). После остановки —
          красная плашка с причиной и временем: раньше блок молча переходил в
          «Отправить», и ученик отправлял обрывок, не поняв, что случилось. */}
      {micLoss && recording && (
        <p role="status" className="text-sm font-medium text-error">
          {t('blockRenderers.recordMicSilentLive')}
        </p>
      )}
      {!recording && stopReasonRef.current && isUnexpectedStop(stopReasonRef.current) && (
        <p role="alert" className="text-sm font-medium text-error">
          {t(stopReasonKey(stopReasonRef.current) as string, {
            time: clock(micLoss ? micLoss.atMs / 1000 : duration ?? 0),
          })}
        </p>
      )}
      {/* ⚠️ Черновик нашёлся, но файл в нём не получился — он уже стёрт. Одна
          строка, без плеера и без отправки: слушать и отправлять там нечего. */}
      {draftLost && !audioURL && !recording && (
        <p role="alert" className="text-sm font-medium text-error">
          {t('blockRenderers.recordDraftLost')}
        </p>
      )}
      {audioURL && (
        <>
          {/* Свою запись мы засекли по часам; у восстановленной со сервера
              длину досчитает сам проигрыватель. */}
          {/* Сперва — длина по файлу от сервера, и только пока её нет — свои
              часы. У восстановленной со сервера записи длину досчитает сам
              проигрыватель (это тоже длина файла, а не часов). */}
          {/* ⚠️ Черновик — подстраховка, а не обещание: Safari на iPhone чистит
              хранилище сайта сам, и сказать об этом ученику честнее, чем
              промолчать и однажды не показать ничего. */}
          {fromDraft && !saved && verdict?.kind !== 'broken' && (
            <p role="status" className="text-sm font-medium text-on-surface-variant">
              {t('blockRenderers.recordDraftFound', { time: clock(duration ?? 0) })}{' '}
              <span className="text-on-surface-variant/80">{t('blockRenderers.recordDraftNote')}</span>
            </p>
          )}
          {/* ⚠️ Файл не получился — плеера нет: он написал бы «не загрузилась» под
              предложением отправить (13.09.2026). */}
          {verdict?.kind !== 'broken' && (
            <AudioPlayer
              src={audioURL}
              knownSeconds={serverRecording?.server_seconds ?? duration}
              onDuration={fresh ? undefined : setRestoredSeconds}
            />
          )}
          {savedProblem ? (
            // Причина — у самого задания, красным; «Записать заново» — кнопка выше.
            <p role="alert" className="text-sm font-medium text-error">
              {t('blockRenderers.audioTooShortSaved')}
            </p>
          ) : saved ? (
            queued ? (
              // ⚠️ ТРИ ЧЕСТНЫХ СОСТОЯНИЯ (решение владельца 02.10.2026): «Сохранено на устройстве» — запись
              // лежит и ждёт связи; «Отправляем N %» — уходит прямо сейчас; «Отправлено» — сервер принял.
              // Раньше второго здесь не было вовсе: повтор шёл молча под надписью «уйдёт, когда появится
              // связь», и первое оставалось на экране даже после того, как запись дошла.
              <div className="space-y-2">
                {sending && sending.phase !== 'checking' ? (
                  <div className="space-y-1" role="status">
                    <span className="flex items-center gap-1.5 text-sm font-medium text-on-surface-variant">
                      <span className="material-symbols-outlined text-base">cloud_sync</span>
                      {/* Свои ключи `record*` — языком урока, как остальные надписи блока (техдолг 386, промпт 12):
                          общие `send*` идут языком интерфейса и остаются у домашки. */}
                      {sending.phase === 'marking'
                        ? t('blockRenderers.recordSendMarking')
                        : sending.percent == null
                          ? t('blockRenderers.recordSendUploadingPlain')
                          : t('blockRenderers.recordSendUploading', { percent: sending.percent })}
                    </span>
                    {sending.phase === 'uploading' && sending.percent != null && (
                      <div className="h-1 w-full overflow-hidden rounded-full bg-surface-container-high">
                        <div className="h-full bg-primary" style={{ width: `${sending.percent}%` }} />
                      </div>
                    )}
                  </div>
                ) : queuedProblem ? (
                  <p role="alert" className="text-sm font-medium text-error">
                    {t(queuedProblem === 'stale' ? 'blockRenderers.audioQueuedStale' : 'blockRenderers.audioQueuedFailed')}
                  </p>
                ) : (
                  <span className="flex items-center gap-1.5 text-sm font-medium text-on-surface-variant">
                    <span className="material-symbols-outlined text-base">cloud_off</span>
                    {t('blockRenderers.audioQueued')}
                  </span>
                )}
                {/* Запись, которая лежит дольше недели или отказана, не стирается сама — только учеником. */}
                {queuedProblem && !sending && (
                  <div className="flex flex-wrap items-center gap-2">
                    {queuedProblem === 'stale' && (
                      <Button size="sm" variant="secondary" onClick={sendQueuedNow}>
                        {t('blockRenderers.audioSendNow')}
                      </Button>
                    )}
                    <button
                      type="button"
                      onClick={deleteQueued}
                      aria-label={t('blockRenderers.recordDiscard')}
                      className="grid h-11 w-11 flex-none place-items-center rounded-button text-error transition hover:bg-error-container"
                    >
                      <span className="material-symbols-outlined">delete</span>
                    </button>
                  </div>
                )}
                {offlineHint && (
                  <Link
                    to="/app/profile/settings#offline-records"
                    className="block text-sm font-medium text-primary underline-offset-2 hover:underline"
                  >
                    {t('blockRenderers.offlineHint')}
                  </Link>
                )}
              </div>
            ) : (
              <span className="flex items-center gap-1.5 text-sm font-medium text-success">
                <span className="material-symbols-outlined text-base">check_circle</span>
                {/* У записи, сделанной сейчас, — «Отправлено»; у пришедшей с сервера при открытии — «Сохранено».
                    Своё `recordSaved`, не общее `saved`: обе надписи — языком урока, как весь блок (техдолг 386;
                    кадр стенда 19.10 поймал русское «Сохранено» под английским заданием). */}
                {t(fresh ? 'blockRenderers.audioSent' : 'blockRenderers.recordSaved')}
              </span>
            )
          ) : tooShort ? (
            // Слишком короткую запись сохранить нельзя — превью остаётся, можно
            // переслушать и перезаписать.
            <p className="text-sm text-on-surface-variant">
              {t('blockRenderers.audioMinSeconds', { seconds: MIN_RECORDING_SECONDS })}
            </p>
          ) : (
            <>
              {/* ⚠️ Вердикт по СОБСТВЕННОЙ длине файла. Пустой файл и оборванная
                  запись закрывают отправку; «замер не удался» только
                  предупреждает — переписывать работу из-за нашей поломки ученик
                  не должен, сервер померит сам (решение владельца §5.1). */}
              {verdict?.messageKey && (
                <p
                  role={verdict.canSave ? 'status' : 'alert'}
                  className={cn(
                    'text-sm font-medium',
                    verdict.canSave ? 'text-on-surface-variant' : 'text-error',
                  )}
                >
                  {t(verdict.messageKey, verdict.params)}
                </p>
              )}
              {/*
                Что происходит с отправкой — словами и полосой.

                ⚠️ ПОЛОСА ПОКАЗЫВАЕТ ТО, ЧТО ЕСТЬ, А НЕ БОДРОСТЬ. Процента нет,
                пока идёт проверка записи и пока отмечается ответ: там нечего
                считать. Нет его и у загрузки, если сервер не назвал размер, —
                тогда просто «Отправляю…».

                ⚠️ ПУГАТЬ УХОДОМ ЗДЕСЬ НЕЧЕМ: запись легла в очередь ещё до
                начала загрузки, и её переживает даже закрытие вкладки. Поэтому
                строка говорит «продолжится сама», а окна подтверждения нет —
                в отличие от домашки, где загрузка идёт мимо очереди.
              */}
              {sending && (
                <div className="mb-2 space-y-1" role="status">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-on-surface-variant">
                      {sending.phase === 'checking'
                        ? t('blockRenderers.recordSendChecking')
                        : sending.phase === 'marking'
                          ? t('blockRenderers.recordSendMarking')
                          : sending.percent == null
                            ? t('blockRenderers.recordSendUploadingPlain')
                            : t('blockRenderers.recordSendUploading', { percent: sending.percent })}
                    </span>
                  </div>
                  {sending.phase === 'uploading' && sending.percent != null && (
                    <div className="h-1 w-full overflow-hidden rounded-full bg-surface-container-high">
                      <div className="h-full bg-primary" style={{ width: `${sending.percent}%` }} />
                    </div>
                  )}
                  <p className="text-[11px] text-on-surface-variant">{t('blockRenderers.recordSendKeepsGoing')}</p>
                </div>
              )}
              <div className="flex flex-wrap items-center gap-2">
                {sendBlocked ? (
                  canOverride && (
                    // Дописать к готовому файлу нельзя — у каждой записи свой
                    // заголовок, и склейка читается только до первого куска.
                    // Поэтому «продолжить» здесь значит «переслушай и реши сам»
                    // (решение владельца 12.09.2026).
                    <Button size="sm" variant="secondary" onClick={() => setAccepted(true)}>
                      {t('blockRenderers.recordListenAndDecide')}
                    </Button>
                  )
                ) : (
                  <Button size="sm" loading={saving} onClick={saveRecording}>
                    {t('blockRenderers.audioSave')}
                  </Button>
                )}
                <button
                  type="button"
                  onClick={discardRecording}
                  aria-label={t('blockRenderers.recordDiscard')}
                  className="grid h-11 w-11 flex-none place-items-center rounded-button text-error transition hover:bg-error-container"
                >
                  <span className="material-symbols-outlined">delete</span>
                </button>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

// ---- Quiz ----

// QuizOption is the structured option shape (Edvibe-style: each option carries
// its own `correct` flag, so there's no fragile numeric answer index and
// MULTIPLE correct options are possible).
export type QuizOption = { text: string; correct: boolean };

// normalizeQuiz reads BOTH the new structured shape and the legacy shape
// ({options: string[], answer: index}) so old blocks and AI-generated drill
// items keep working.
export function normalizeQuizOptions(c: {
  options?: unknown;
  answer?: number;
}): QuizOption[] {
  const raw = c.options;
  if (Array.isArray(raw) && raw.length > 0 && typeof raw[0] === 'object') {
    return (raw as QuizOption[]).map((o) => ({ text: String(o?.text ?? ''), correct: !!o?.correct }));
  }
  const legacy = (raw as string[] | undefined) ?? [];
  return legacy.map((text, i) => ({ text, correct: i === c.answer }));
}

export type QuizQuestion = { text?: string; options: QuizOption[]; explanation?: string };

// normalizeQuizQuestions reads the new multi-question shape
// ({questions:[{text,options,explanation}]}) AND the legacy single-question
// shape ({question, options, answer, explanation}) so old blocks and
// AI-generated drill items keep working with zero migration.
export function normalizeQuizQuestions(content: unknown): QuizQuestion[] {
  const c = (content ?? {}) as {
    questions?: Array<{ text?: string; question?: string; options?: unknown; answer?: number; explanation?: string }>;
    question?: string;
    options?: unknown;
    answer?: number;
    explanation?: string;
  };
  if (Array.isArray(c.questions) && c.questions.length > 0) {
    return c.questions.map((q) => ({
      text: q.text ?? q.question ?? '',
      options: normalizeQuizOptions(q),
      explanation: q.explanation,
    }));
  }
  return [{ text: c.question ?? '', options: normalizeQuizOptions(c), explanation: c.explanation }];
}

// A single quiz question: owns its own pick/reveal state and reports its result
// (correct? + raw answer_data) up to the parent block. `locked` forces a reveal
// when a block-level timer expires (unanswered → wrong).
// Имя отличается от типа `QuizQuestion` намеренно: раньше тип и компонент
// назывались одинаково. TypeScript такое допускает — типы и значения живут в
// разных пространствах имён, — но читателю приходится гадать, что перед ним.
const QuizQuestionCard = ({
  q,
  index,
  locked,
  onResult,
  onActivity,
  initial,
}: {
  q: QuizQuestion;
  index: number;
  locked: boolean;
  onResult: (index: number, correct: boolean, answerData: string) => void;
  // Первый выбор ученика: с него, а не с появления блока на экране, начинает
  // идти таймер (21.09.2026, договор quiz-timer.md).
  onActivity?: () => void;
  // Saved answer_data for this question ("2" for single-select, "[0,2]" for
  // multi-select), used to restore the pick + reveal after a refresh.
  initial?: string;
}) => {
  const { t } = useTranslation();
  const options = q.options;
  const correctIdx = useMemo(() => options.map((o, i) => (o.correct ? i : -1)).filter((i) => i >= 0), [options]);
  const multi = correctIdx.length > 1; // several correct → checkbox mode
  const [sel, setSel] = useState<Set<number>>(() => {
    if (initial != null && multi) {
      try {
        const a = JSON.parse(initial);
        if (Array.isArray(a)) return new Set(a.map(Number));
      } catch { /* not restorable */ }
    }
    return new Set();
  });
  const [picked, setPicked] = useState<number | null>(() => {
    if (initial != null && !multi && initial !== '') {
      const n = Number(initial);
      return Number.isFinite(n) ? n : null;
    }
    return null;
  });
  const [done, setDone] = useState(initial != null);
  const isRight = multi
    ? sel.size === correctIdx.length && correctIdx.every((i) => sel.has(i))
    : picked !== null && !!options[picked]?.correct;

  // ⚠️ ВРЕМЯ ВЫШЛО — ЭТО НЕ ОТВЕТ (21.09.2026, договор quiz-timer.md).
  //
  // До этого дня истёкший таймер отвечал за ученика: вопрос без выбора
  // отчитывался пустой строкой и уходил в ответ блока как неверный. Ученик
  // видел «Неверно» за то, чего не делал, а раздел закрывался сам
  // (`quiz_with_timer` — в ANSWERABLE_TYPES). На проде так записалось 628
  // ответов из 1328 по этому типу, у 574 учеников.
  //
  // Теперь: выбор есть — отчитываемся, как раньше; выбора нет — вопрос просто
  // запирается и показывает «Время вышло», наверх не уходит ничего.
  const [expired, setExpired] = useState(false);
  useEffect(() => {
    if (!locked || done) return;
    const hasPick = multi ? sel.size > 0 : picked !== null;
    if (!hasPick) {
      setExpired(true);
      return;
    }
    setDone(true);
    const ok = multi
      ? sel.size === correctIdx.length && correctIdx.every((i) => sel.has(i))
      : !!options[picked as number]?.correct;
    const data = multi ? JSON.stringify([...sel].sort((a, b) => a - b)) : String(picked);
    onResult(index, ok, data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locked]);

  const submitSingle = (idx: number) => {
    onActivity?.();
    setPicked(idx);
    setDone(true);
    // answer_data = String(index) for single-answer so the server drill grader
    // (which compares to the correct index) stays compatible.
    onResult(index, !!options[idx]?.correct, String(idx));
  };
  const submitMulti = () => {
    setDone(true);
    const ok = sel.size === correctIdx.length && correctIdx.every((i) => sel.has(i));
    onResult(index, ok, JSON.stringify([...sel].sort((a, b) => a - b)));
  };

  return (
    <div className="space-y-2">
      <RichText text={q.text} className="font-semibold text-on-surface" />
      {/* 6px между вариантами: отступ задаёт только сетка (у .ex-opt нет поля). */}
      <ul className="grid gap-1.5">
        {options.map((opt, idx) => {
          const chosen = multi ? sel.has(idx) : picked === idx;
          return (
            <li key={idx}>
              <button
                // Время вышло — выбирать уже нельзя: иначе ответ ушёл бы после
                // истечения таймера.
                disabled={done || expired}
                onClick={() => {
                  if (!multi) {
                    submitSingle(idx);
                    return;
                  }
                  // Отметка в множественном выборе — тоже начало работы: с неё
                  // идёт таймер блока.
                  onActivity?.();
                  setSel((s) => {
                    // Переключаем выбор: было отмечено — снимаем, не было — ставим.
                    const n = new Set(s);
                    if (n.has(idx)) n.delete(idx);
                    else n.add(idx);
                    return n;
                  });
                }}
                className={cn(
                  'ex-opt w-full text-left',
                  done && opt.correct && 'ex-opt--ok',
                  done && chosen && !opt.correct && 'ex-opt--bad',
                  !done && chosen && 'bg-primary-container',
                )}
              >
                {multi ? (
                  <span className={cn('material-symbols-outlined text-base', chosen ? 'text-primary' : 'text-on-surface-variant')}>
                    {chosen ? 'check_box' : 'check_box_outline_blank'}
                  </span>
                ) : (
                  <span className={cn(
                    'flex h-[18px] w-[18px] flex-none items-center justify-center rounded-full border-2',
                    done && opt.correct ? 'border-success' : chosen ? 'border-primary' : 'border-outline-variant',
                  )}>
                    {(chosen || (done && opt.correct)) && (
                      <span className={cn('h-2 w-2 rounded-full', done && opt.correct ? 'bg-success' : 'bg-primary')} />
                    )}
                  </span>
                )}
                <span className="flex-1 whitespace-pre-wrap">{opt.text}</span>
              </button>
            </li>
          );
        })}
      </ul>
      {multi && !done && !expired && (
        <Button size="sm" onClick={submitMulti} disabled={sel.size === 0}>
          {t('blockRenderers.check')}
        </Button>
      )}
      {expired && !done && (
        <p className="text-on-surface-variant">{t('blockRenderers.timeUp')}</p>
      )}
      {done && (
        <div className="space-y-1">
          <p className={isRight ? 'text-success' : 'text-error'}>
            {isRight ? t('blockRenderers.correct') : t('blockRenderers.incorrectRetry')}
          </p>
          <RichText text={q.explanation} className="text-sm text-on-surface-variant" />
        </div>
      )}
    </div>
  );
};

const QuizBlock: Renderer = ({ block, onAnswer, saved }) => {
  const { t } = useTranslation();
  const c = block.content as { instruction?: string; seconds?: number };
  const questions = useMemo(() => normalizeQuizQuestions(block.content), [block.content]);
  const single = questions.length === 1;
  // Per-question saved picks, so a refresh restores exactly what was chosen.
  const initials = useMemo(() => savedItemValues(saved, single), [saved, single]);

  // Per-question results + picks; when every question has reported, emit the
  // block answer carrying the ACTUAL picks (so it can be restored later).
  const [, setState] = useState<{ ok: (boolean | null)[]; data: (string | null)[] }>(() => ({
    ok: questions.map(() => null),
    data: questions.map(() => null),
  }));
  // Ответ блока уходит один раз: и по последнему отвеченному вопросу, и по
  // истечению таймера — смотря что случится раньше.
  const sentRef = useRef(false);
  const sendAnswer = (ok: (boolean | null)[], data: (string | null)[]) => {
    if (sentRef.current) return;
    // ⚠️ Пустой ответ на сервер не уходит: без единого выбора `send` = false
    // (lib/quizTimer.ts, договор quiz-timer.md). Частичный ответ уходит как
    // есть — труд ученика не пропадает, неотвеченные остаются пустыми местами.
    const answer = buildQuizAnswer(data, single);
    if (!answer.send) return;
    sentRef.current = true;
    onAnswer?.({ answer_data: answer.answer_data, is_correct: ok.every((r) => r === true) });
  };
  const onResult = (index: number, correct: boolean, answerData: string) => {
    setState((prev) => {
      if (prev.ok[index] !== null) return prev; // already recorded
      const ok = [...prev.ok];
      ok[index] = correct;
      const data = [...prev.data];
      data[index] = answerData;
      // Single question keeps its raw answer_data (String(index) / '[…]'); a
      // multi-question block stores the array of per-question picks.
      if (ok.every((r) => r !== null)) sendAnswer(ok, data);
      return { ok, data };
    });
  };

  // Optional block-level timer (quiz_with_timer).
  //
  // ⚠️ ОТСЧЁТ ИДЁТ С ПЕРВОГО ВЫБОРА, А НЕ С ПОЯВЛЕНИЯ БЛОКА (21.09.2026).
  // Раньше он начинался при отрисовке: блок мог просто побыть на экране четыре
  // минуты, и урок получал пять неверных ответов, которых ученик не давал.
  const timed = typeof c.seconds === 'number' && c.seconds > 0;
  const [started, setStarted] = useState(false);
  const [remaining, setRemaining] = useState<number>(timed ? (c.seconds as number) : 0);
  const [locked, setLocked] = useState(false);
  useEffect(() => {
    if (!timed || !started || locked) return;
    if (remaining <= 0) {
      setLocked(true);
      return;
    }
    const id = setInterval(() => setRemaining((r) => Math.max(0, r - 1)), 1000);
    return () => clearInterval(id);
  }, [timed, started, locked, remaining]);

  // Время вышло: отправляем то, что ученик успел выбрать. Не выбрал ничего —
  // не уходит ничего, вопросы показывают «Время вышло».
  //
  // ⚠️ Состояние читаем в очереди обновления, а не из замыкания: карточки
  // вопросов отчитываются в своих эффектах, а они срабатывают РАНЬШЕ этого,
  // родительского. Снимок из замыкания ещё не содержал бы их выборов, и
  // ответ ушёл бы пустым — ровно то, что чиним.
  useEffect(() => {
    if (!locked) return;
    setState((prev) => {
      sendAnswer(prev.ok, prev.data);
      return prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locked]);

  return (
    <div className="space-y-4">
      <Instruction text={c.instruction} />
      {timed && (
        <Pill variant={remaining <= 5 ? 'danger' : 'warning'}>
          <span className="material-symbols-outlined text-sm">timer</span>
          {t('blockRenderers.seconds', { n: remaining })}
        </Pill>
      )}
      {questions.map((q, i) => (
        <div key={i} className={cn('space-y-2', i > 0 && 'border-t border-outline-variant/60 pt-4')}>
          {questions.length > 1 && (
            <span className="text-xs font-semibold uppercase tracking-wide text-on-surface-variant">
              {i + 1} / {questions.length}
            </span>
          )}
          <QuizQuestionCard
            q={q}
            index={i}
            locked={locked}
            onResult={onResult}
            onActivity={() => setStarted(true)}
            initial={initials[i]}
          />
        </div>
      ))}
    </div>
  );
};

export type TFAnswer = 'true' | 'false' | 'not_given';
export type TFStatement = { text: string; answer: TFAnswer };

// Reads both the new {statements:[{text,answer}]} shape and the legacy
// {statement, answer} single-statement shape (still emitted by drill generation).
export function normalizeTFStatements(content: unknown): TFStatement[] {
  const c = (content ?? {}) as {
    statements?: Array<{ text?: string; answer?: string }>;
    statement?: string;
    answer?: string;
  };
  const norm = (a?: string): TFAnswer =>
    a === 'false' || a === 'not_given' ? a : 'true';
  if (Array.isArray(c.statements) && c.statements.length > 0) {
    return c.statements.map((s) => ({ text: s.text ?? '', answer: norm(s.answer) }));
  }
  return [{ text: c.statement ?? '', answer: norm(c.answer) }];
}

const TrueFalseBlock: Renderer = ({ block, onAnswer, saved }) => {
  const statements = useMemo(() => normalizeTFStatements(block.content), [block.content]);
  const { explanation, instruction } = (block.content as { explanation?: string; instruction?: string }) ?? {};
  // Restore: answer_data holds the picked True/False/Not-Given per statement.
  const savedPicks = useMemo<(TFAnswer | null)[] | null>(() => {
    if (!saved) return null;
    if (statements.length === 1) return [saved.answer_data as TFAnswer];
    try {
      const a = JSON.parse(saved.answer_data);
      if (Array.isArray(a) && a.length === statements.length) return a as (TFAnswer | null)[];
    } catch { /* not restorable */ }
    return null;
  }, [saved, statements.length]);
  const [picks, setPicks] = useState<(TFAnswer | null)[]>(() => savedPicks ?? statements.map(() => null));
  // Вариант «True / False» (`not_given: false`) — две кнопки (lib/trueFalse.ts, 13.10.2026).
  const labels: { v: TFAnswer; l: string }[] = [
    { v: 'true', l: 'True' },
    { v: 'false', l: 'False' },
    ...(tfNotGivenAllowed(block.content) ? [{ v: 'not_given' as const, l: 'Not Given' }] : []),
  ];
  const allDone = picks.every((p) => p !== null);
  const pick = (i: number, v: TFAnswer) => {
    if (picks[i] !== null) return;
    const next = [...picks];
    next[i] = v;
    setPicks(next);
    if (next.every((p) => p !== null)) {
      const allRight = statements.every((s, idx) => next[idx] === s.answer);
      // Single statement keeps answer_data = the raw answer so the drill grader
      // (which compares answer_data to content.answer) stays unaffected.
      const answer_data =
        statements.length === 1 ? (next[0] as string) : JSON.stringify(next);
      onAnswer?.({ answer_data, is_correct: allRight });
    }
  };
  return (
    <div className="space-y-4">
      <Instruction text={instruction} />
      {statements.map((s, i) => {
        const done = picks[i] !== null;
        const right = picks[i] === s.answer;
        return (
          <div key={i} className="rounded-2xl bg-surface-container-lowest p-4 text-center">
            {s.text && <p className="mb-3 whitespace-pre-wrap font-semibold text-on-surface">{s.text}</p>}
            <div className="flex flex-wrap justify-center gap-2.5">
              {labels.map((opt) => {
                const sel = picks[i] === opt.v;
                const state = done && opt.v === s.answer
                  ? 'ex-pill--ok'
                  : done && sel && !right
                    ? 'ex-pill--bad'
                    : sel
                      ? 'ex-pill--sel'
                      : '';
                return (
                  <button
                    key={opt.v}
                    type="button"
                    disabled={done}
                    onClick={() => pick(i, opt.v)}
                    className={cn('ex-pill', state)}
                  >
                    {opt.l}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
      {allDone && <RichText text={explanation} className="text-sm text-on-surface-variant" />}
    </div>
  );
};

// ---- Fill / order (simplified) ----

// One or many "type the word into the gap" sentences per block.
type GapItem = { sentence?: string; answer?: string };
function normalizeGapItems(content: unknown): GapItem[] {
  const c = (content ?? {}) as { items?: GapItem[]; sentence?: string; answer?: string };
  if (Array.isArray(c.items) && c.items.length > 0) {
    return c.items.map((it) => ({ sentence: it?.sentence, answer: it?.answer }));
  }
  return [{ sentence: c.sentence, answer: c.answer }];
}

const InputGapItem = ({
  item,
  index,
  report,
  initial,
  caseSensitive = false,
}: {
  item: GapItem;
  index: number;
  report: ItemReport;
  initial?: string;
  /** Флаг автора «учитывать регистр» (`content.case_sensitive`, 13.10.2026). */
  caseSensitive?: boolean;
}) => {
  const { t } = useTranslation();
  const [val, setVal] = useState(initial ?? '');
  const [done, setDone] = useState(initial != null);
  const [restored, setRestored] = useState(initial != null);
  // ⚠️ Сверка общая с сервером: lib/normalizeAnswer.ts ↔ shared/textnorm.
  // Своего сравнения здесь быть не должно — сервер ПЕРЕПРОВЕРЯЕТ ответ и
  // заменяет вердикт браузера своим, а расхождение даёт худший случай:
  // галочка на экране и ошибка в базе. Варианты через «/» в ключе — как
  // progress-service slash_variants.go (lessonAnswerMatch).
  const correct = lessonAnswerMatch(val, item.answer ?? '', caseSensitive);
  return (
    <div className="space-y-2">
      <p className="whitespace-pre-wrap text-on-surface">{item.sentence}</p>
      <div className="flex gap-2">
        <input
          value={val}
          onChange={(e) => { setRestored(false); setVal(e.target.value); }}
          disabled={done}
          className="flex-1 rounded-xl border-2 border-info/25 bg-surface-container-lowest px-4 py-2.5 text-on-surface outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20"
          placeholder={t('blockRenderers.inputPlaceholder')}
        />
        {!done && (
          <Button onClick={() => setDone(true)} disabled={!val}>
            {t('blockRenderers.check')}
          </Button>
        )}
      </div>
      {done && (
        <RetryResult correct={correct} answer={item.answer} answerData={val} silent={restored} onRetry={() => { setRestored(false); setVal(''); setDone(false); }} onAnswer={(p, silent) => report(index, p.is_correct, p.answer_data, !silent)} />
      )}
    </div>
  );
};

const InputGapBlock: Renderer = ({ block, onAnswer, saved }) => {
  const c = block.content as { instruction?: string };
  const items = useMemo(() => normalizeGapItems(block.content), [block.content]);
  const report = useBlockResult(items.length, items.length === 1, onAnswer);
  const initials = useMemo(() => savedItemValues(saved, items.length === 1), [saved, items.length]);
  return (
    <div className="space-y-4">
      <Instruction text={c.instruction} />
      {items.map((it, i) => (
        <MultiItem key={i} index={i} total={items.length}>
          <InputGapItem item={it} index={i} report={report} initial={initials[i]} caseSensitive={caseSensitiveOf(block.content)} />
        </MultiItem>
      ))}
    </div>
  );
};

// ---- Multi-gap fill (Edvibe flagship) ----

// Разбор шаблона — lib/gapTemplate.ts (общий с «Дополни таблицу», 13.10.2026).
export { parseGapTemplate, type GapSegment };

/** Флаг автора «учитывать регистр» (13.10.2026): только явное `true`, по умолчанию — без регистра. */
const caseSensitiveOf = (content: unknown): boolean =>
  (content as { case_sensitive?: unknown } | null | undefined)?.case_sensitive === true;

type GapDef = Extract<GapSegment, { kind: 'gap' }>;
type GapMode = 'input' | 'dropdown' | 'drag';

/**
 * Состояние пропусков одного задания: значения, банк, раскладка, проверка.
 *
 * ⚠️ ОБЩЕЕ У «ПРОПУСКОВ В ТЕКСТЕ» И «ДОПОЛНИ ТАБЛИЦУ» (13.10.2026). Таблица — те же
 * пропуски, разложенные по ячейкам: банк с повторами, касание «слово → место», сверка
 * с сервером, восстановление ответа. Вторая копия этой логики разошлась бы с первой
 * при первой же правке — поэтому одна, а различается только раскладка.
 */
function useGapSession(
  gaps: GapDef[],
  c: { mode?: GapMode; distractors?: string[] },
  caseSensitive: boolean,
  saved: RenderProps['saved'],
  onAnswer: RenderProps['onAnswer'],
) {
  const mode = c.mode ?? 'input';
  // Restore: answer_data already stores the per-gap values (JSON array), so a
  // refresh re-fills every gap and reveals the ✓/✗ colouring.
  const savedVals = useMemo(() => {
    if (!saved) return null;
    try {
      const a = JSON.parse(saved.answer_data);
      if (Array.isArray(a) && a.length === gaps.length) return a.map((x) => String(x));
    } catch { /* not restorable */ }
    return null;
  }, [saved, gaps.length]);
  const [vals, setVals] = useState<string[]>(() => savedVals ?? gaps.map(() => ''));
  const [done, setDone] = useState(savedVals != null);
  const [overGap, setOverGap] = useState<number | null>(null);

  // Shared pool for dropdown / drag = each gap's primary answer + every gap's
  // inline distractors (Edvibe "[a/b*/c]") + the block's shared distractor list.
  // ⚠️ Одинаковые ответы двух пропусков — две плашки (lib/gapBank.ts, 13.10.2026).
  const pool = useMemo(() => shuffle(gapBank(gaps, c.distractors ?? [])), [gaps, c.distractors]);

  // ⚠️ РЕЖИМ ПЕРЕТАСКИВАНИЯ ДЕРЖИТ РАСКЛАДКУ, А НЕ СТРОКИ: слово — номер в банке,
  // пропуск — номер места. Касание и перетаскивание идут через lib/tapPlace.ts.
  // До 10.09.2026 касание слова клало его в ПЕРВЫЙ пустой пропуск: на телефоне,
  // где перетаскивания нет, ученик не мог выбрать место и получал «верные слова
  // не в тех пропусках» — так выглядели все 74 неверных ответа из 1251.
  const scope = useId();
  const placementFrom = (vals: string[] | null): Placement => {
    const s = emptyPlacement(pool.length);
    if (mode !== 'drag' || !vals) return s;
    const at = [...s.at];
    vals.forEach((v, gi) => {
      const j = pool.findIndex((w, k) => w === v && at[k] === null);
      if (j >= 0) at[j] = gi;
    });
    return { at, sel: null };
  };
  const [placement, setPlacement] = useState<Placement>(() => placementFrom(savedVals));

  // ⚠️ СОХРАНЁННЫЙ ОТВЕТ МОЖЕТ ПРИЙТИ ПОЗЖЕ ПЕРВОЙ ОТРИСОВКИ (стенд 15.10.2026). В разборе
  // куратора первый раздел рисуется раньше, чем загружены ответы ученика, а useState берёт
  // начальное значение один раз — поля стояли пустыми при плашке «Неверно». Подхватываем
  // ответ, только пока ученик сам ничего не ввёл и не проверил, — как matching.
  const savedKey = savedVals ? JSON.stringify(savedVals) : '';
  useEffect(() => {
    if (!savedVals || done) return;
    if (vals.some((v) => v !== '') || placement.at.some((p) => p !== null)) return;
    setVals(savedVals);
    setPlacement(placementFrom(savedVals));
    setDone(true);
    // только при появлении сохранённого ответа
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedKey]);
  const shown =
    mode === 'drag'
      ? gaps.map((_, i) => {
          const o = occupant(placement, i);
          return o >= 0 ? pool[o] : '';
        })
      : vals;
  const dropOn = (place: number | null, token: string) => {
    if (done) return;
    const item = readDragToken(scope, token);
    if (item !== null) setPlacement((s) => movePlaced(s, item, place, 1));
  };

  // Per-gap dropdown options: a gap that carries its OWN inline distractors
  // (star-syntax) shows just its answer + those distractors; a classic [[...]]
  // gap (null here) falls back to the shared pool.
  const gapOptions = useMemo(
    () =>
      gaps.map((g) =>
        g.distractors && g.distractors.length
          ? shuffle(Array.from(new Set([...g.answers.filter(Boolean), ...g.distractors])))
          : null,
      ),
    [gaps],
  );

  // ⚠️ Сверка общая с сервером: lib/normalizeAnswer.ts ↔ shared/textnorm
  // (gradeGapFill). Сервер перепроверяет пропуски и заменяет вердикт браузера
  // своим — разойдясь, мы получим галочку на экране и ошибку в базе. Каждый
  // ответ [[…|…]] знает варианты через «/» — как slash_variants.go; у
  // выпадающего [a/*/b] черты в ответе после разбора нет, он не задет.
  const isGapRight = (i: number) => (gaps[i]?.answers ?? []).some((a) => lessonAnswerMatch(shown[i] ?? '', a, caseSensitive));
  const setGap = (i: number, v: string) => setVals((p) => p.map((x, j) => (j === i ? v : x)));
  const allRight = gaps.every((_, i) => isGapRight(i));

  const submit = () => {
    setDone(true);
    setPlacement((s) => ({ ...s, sel: null }));
    onAnswer?.({ answer_data: JSON.stringify(shown), is_correct: gaps.every((_, i) => isGapRight(i)) });
  };
  const retry = () => {
    setVals(gaps.map(() => ''));
    setPlacement(emptyPlacement(pool.length));
    setDone(false);
  };

  return {
    mode, gaps, vals, setGap, done, overGap, setOverGap, pool, placement, setPlacement, scope, shown, dropOn,
    gapOptions, isGapRight, allRight, submit, retry,
  };
}
type GapSession = ReturnType<typeof useGapSession>;

/** Банк слов режима перетаскивания — над текстом (или таблицей), скрыт, когда всё разложено. */
function GapBank({ s }: { s: GapSession }) {
  const { mode, placement, done, setPlacement, dropOn, pool, scope } = s;
  return (
    <>
      {/* ⚠️ БАНК СЛОВ ИДЁТ НАД ТЕКСТОМ, А НЕ ПОД НИМ (просьба владельца 12.09.2026).
          Порядок отвечает тому, как задание решают: сначала выбрал слово, потом место.
          Раньше банк стоял под абзацем, и на телефоне при длинном тексте ученик его
          просто не видел — прокручивал вниз, только догадавшись, что слова где-то есть.

          Касание пустого места банка возвращает выделенное слово; сюда же его можно
          бросить мышью.

          Банк скрывается целиком, когда всё расставлено и ничего не выделено: пустая
          рамка на экране только сбивает. Так же сделано в «словах к картинкам».
          Выделенное слово держит банк видимым — иначе выделение некуда вернуть. */}
      {mode === 'drag' && (placement.at.some((v) => v === null) || placement.sel !== null) && (
        <div
          className="ex-bank"
          onClick={() => { if (!done) setPlacement((s) => tapBank(s)); }}
          onDragOver={(e) => { if (!done) e.preventDefault(); }}
          onDrop={(e) => { e.preventDefault(); dropOn(null, e.dataTransfer.getData('text/plain')); }}
        >
          {pool.map((w, j) => (
            <DragChip
              // По номеру: в банке бывают два одинаковых слова, ключ по тексту их склеивал.
              key={j}
              value={w}
              payload={dragToken(scope, j)}
              disabled={done || placement.at[j] !== null}
              selected={placement.sel === j}
              onClick={(e) => {
                e.stopPropagation();
                if (!done) setPlacement((s) => tapItem(s, j));
              }}
              className="py-1.5"
            />
          ))}
        </div>
      )}
    </>
  );
}

/**
 * Одно место пропуска: выпадающий список, место для слова из банка или поле ввода.
 * `wide` — пропуск во всю ширину (ячейка таблицы целиком, карточка на телефоне).
 */
function GapSlot({ s, i, wide = false, label }: { s: GapSession; i: number; wide?: boolean; label?: string }) {
  const { mode, vals, setGap, done, overGap, setOverGap, pool, placement, setPlacement, scope, shown, dropOn, gapOptions, isGapRight } = s;
  const stateCls = done ? (isGapRight(i) ? 'ex-gap--ok' : 'ex-gap--bad') : '';
  if (mode === 'dropdown') {
    return (
      <select
        value={vals[i]}
        disabled={done}
        aria-label={label}
        onChange={(e) => setGap(i, e.target.value)}
        className={cn('ex-gap', wide ? 'w-full' : 'mx-1', stateCls)}
      >
        <option value="">—</option>
        {(gapOptions[i] ?? uniqueWords(pool)).map((o) => (
          <option key={o} value={o}>{o}</option>
        ))}
      </select>
    );
  }
  if (mode === 'drag') {
    const occ = occupant(placement, i);
    return (
      <button
        type="button"
        disabled={done}
        aria-label={label}
        // Слово в пропуске можно схватить и перенести — и касанием, и мышью.
        draggable={!done && occ >= 0}
        onDragStart={(e) => e.dataTransfer.setData('text/plain', dragToken(scope, occ))}
        onClick={() => { if (!done) setPlacement((st) => tapSlot(st, i)); }}
        onDragOver={(e) => { if (!done) { e.preventDefault(); setOverGap(i); } }}
        onDragLeave={() => setOverGap((o) => (o === i ? null : o))}
        onDrop={(e) => {
          e.preventDefault();
          setOverGap(null);
          dropOn(i, e.dataTransfer.getData('text/plain'));
        }}
        className={cn(
          'ex-gap inline-flex min-w-[3rem] items-center align-middle',
          wide ? 'w-full justify-start' : 'mx-1 justify-center',
          overGap === i || (occ >= 0 && placement.sel === occ)
            ? 'ex-gap--sel'
            : placement.sel !== null
              ? 'ex-gap--target'
              : stateCls,
        )}
      >
        {shown[i] || '—'}
      </button>
    );
  }
  return (
    <input
      value={vals[i]}
      disabled={done}
      aria-label={label}
      onChange={(e) => setGap(i, e.target.value)}
      className={cn('ex-gap', wide ? 'w-full' : 'mx-1 w-28', stateCls)}
    />
  );
}

/** «Проверить» — пока не проверено; потом итог, «Ещё раз» и верные ответы. */
function GapCheck({ s }: { s: GapSession }) {
  const { t } = useTranslation();
  const { done, submit, shown, allRight, retry, gaps } = s;
  return !done ? (
    <Button onClick={submit} disabled={shown.some((v) => !v)}>
      {t('blockRenderers.check')}
    </Button>
  ) : (
    <div className="space-y-1">
      <div className="flex items-center justify-between gap-2">
        <p className={allRight ? 'text-success' : 'text-error'}>
          {allRight ? t('blockRenderers.correct') : t('blockRenderers.incorrectRetry')}
        </p>
        {!allRight && (
          <Button size="sm" variant="ghost" onClick={retry}>
            <span className="material-symbols-outlined text-base">refresh</span>
            {t('blockRenderers.tryAgain')}
          </Button>
        )}
      </div>
      {!allRight && (
        <p className="text-sm text-on-surface-variant">
          {gaps.map((g) => g.answers[0]).join(', ')}
        </p>
      )}
    </div>
  );
}

const GapFillBlock: Renderer = ({ block, onAnswer, saved }) => {
  const c = block.content as { instruction?: string; template?: string; mode?: GapMode; distractors?: string[] };
  const segments = useMemo(() => parseGapTemplate(c.template ?? ''), [c.template]);
  const gaps = useMemo(() => segments.filter((s): s is GapDef => s.kind === 'gap'), [segments]);
  const s = useGapSession(gaps, c, caseSensitiveOf(block.content), saved, onAnswer);

  let gapIdx = -1;
  return (
    <div className="space-y-3">
      {c.instruction && <RichText text={c.instruction} className="font-medium text-on-surface" />}
      <GapBank s={s} />
      {/* whitespace-pre-wrap: перенос строки, введённый преподавателем, должен
          дойти до ученика. Без него песня или диалог слипаются в один абзац —
          браузер схлопывает переносы в обычном тексте. */}
      <p className="whitespace-pre-wrap leading-loose text-on-surface">
        {segments.map((seg, si) => {
          if (seg.kind === 'text') return <span key={si}>{seg.text}</span>;
          gapIdx += 1;
          return <GapSlot key={si} s={s} i={gapIdx} />;
        })}
      </p>
      <GapCheck s={s} />
    </div>
  );
};

/** Узкий экран для таблицы — 640 px, порог владельца (13.10.2026); без matchMedia — широкий. */
function useNarrowTable(): boolean {
  const query = '(max-width: 639px)';
  const has = () => typeof window !== 'undefined' && typeof window.matchMedia === 'function';
  const [narrow, setNarrow] = useState(() => (has() ? window.matchMedia(query).matches : false));
  useEffect(() => {
    if (!has()) return;
    const mq = window.matchMedia(query);
    const on = () => setNarrow(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  return narrow;
}

/**
 * «Дополни таблицу» (`table_fill`, 13.10.2026; договор docs/CONTRACTS/block-types-1.1.2.md §3).
 *
 * Пропуски, банк, проверка и восстановление — те же, что у «пропусков в тексте»
 * (useGapSession); своё здесь только раскладка (lib/tableFill.ts tableLayout):
 * таблица от 640 px; уже — карточки (заголовок и ≥3 столбцов) или прокрутка вбок
 * с закреплённым первым столбцом. Оформление — классы и токены урока, своих цветов нет.
 */
const TableFillBlock: Renderer = ({ block, onAnswer, saved }) => {
  const c = block.content as TableFillContent;
  const model = useMemo(() => tableModel(c), [c]);
  const s = useGapSession(
    model.gaps,
    { mode: c.mode === 'drag' ? 'drag' : 'input', distractors: c.distractors },
    caseSensitiveOf(block.content),
    saved,
    onAnswer,
  );
  const layout = tableLayout(model.header, model.cols, useNarrowTable());
  const head = model.header ? model.rows[0] : null;
  const body = model.header ? model.rows.slice(1) : model.rows;

  const cellLabel = (ci: number) => (head ? head[ci]?.text : undefined);
  const renderCell = (cell: TableCell, ci: number) => {
    if (cell.wholeGap) return <GapSlot s={s} i={cell.gapStart} wide label={cellLabel(ci)} />;
    let gi = cell.gapStart - 1;
    return (
      <span className="whitespace-pre-wrap leading-loose">
        {cell.segments.map((seg, si) => {
          if (seg.kind === 'text') return <span key={si}>{seg.text}</span>;
          gi += 1;
          return <GapSlot key={si} s={s} i={gi} label={cellLabel(ci)} />;
        })}
      </span>
    );
  };

  return (
    <div className="space-y-3">
      {c.instruction && <RichText text={c.instruction} className="font-medium text-on-surface" />}
      <GapBank s={s} />
      {layout === 'cards' ? (
        // Телефон: строка — карточка, подписи из строки заголовка (как утверждения True/False).
        <div className="space-y-2">
          {body.map((row, ri) => (
            <div key={ri} className="space-y-2 rounded-2xl bg-surface-container-lowest p-4">
              {row.map((cell, ci) => (
                <div key={ci}>
                  <p className="text-xs font-semibold text-on-surface-variant">{head?.[ci]?.text}</p>
                  <div className="text-on-surface">{renderCell(cell, ci)}</div>
                </div>
              ))}
            </div>
          ))}
        </div>
      ) : (
        // ⚠️ Прокрутка вбок — внутри блока, а не страницы (index.css: body без overflow-x).
        <div className="overflow-x-auto rounded-2xl bg-surface-container-lowest">
          <table className="w-full border-collapse text-sm text-on-surface">
            {head && (
              <thead>
                <tr>
                  {head.map((cell, ci) => (
                    <th
                      key={ci}
                      scope="col"
                      className={cn(
                        'border-b border-outline-variant/50 px-3 py-2 text-left text-xs font-semibold text-on-surface-variant',
                        layout === 'scroll' && ci === 0 && 'sticky left-0 z-[1] bg-surface-container-lowest',
                      )}
                    >
                      {cell.text}
                    </th>
                  ))}
                </tr>
              </thead>
            )}
            <tbody>
              {body.map((row, ri) => (
                <tr key={ri} className="border-b border-outline-variant/30 last:border-b-0">
                  {row.map((cell, ci) => (
                    <td
                      key={ci}
                      className={cn(
                        'px-3 py-2 align-middle',
                        layout === 'scroll' && 'min-w-[8rem]',
                        layout === 'scroll' && ci === 0 && 'sticky left-0 z-[1] bg-surface-container-lowest',
                      )}
                    >
                      {renderCell(cell, ci)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <GapCheck s={s} />
    </div>
  );
};

// Caption-the-picture: shows an image (editor writes `image_url`) above a
// text input. Same check/feedback logic as InputGapBlock, no `sentence`.
// One or many "type the word for this picture" items per block.
type ImageItem = { image_url?: string; answer?: string; word?: string; options?: string[] };
function normalizeImageItems(content: unknown): ImageItem[] {
  const c = (content ?? {}) as { items?: ImageItem[]; image_url?: string; answer?: string; word?: string; options?: string[] };
  if (Array.isArray(c.items) && c.items.length > 0) {
    return c.items.map((it) => ({ image_url: it?.image_url, answer: it?.answer, word: it?.word, options: it?.options }));
  }
  return [{ image_url: c.image_url, answer: c.answer, word: c.word, options: c.options }];
}

const WordInputImageItem = ({
  item,
  index,
  report,
  initial,
  checkAll,
  caseSensitive = false,
}: {
  item: ImageItem;
  index: number;
  report: ItemReport;
  initial?: string;
  /** Счётчик нажатий общей кнопки «Проверить»: растёт — значит пора считать. */
  checkAll: number;
  /** Флаг автора «учитывать регистр» (`content.case_sensitive`, 13.10.2026). */
  caseSensitive?: boolean;
}) => {
  const { t } = useTranslation();
  const [val, setVal] = useState(initial ?? '');
  const [done, setDone] = useState(initial != null);
  const [restored, setRestored] = useState(initial != null);

  // Общая кнопка внизу закрывает все заполненные поля разом. Пустые не трогаем:
  // незаполненное поле — это «не ответил», а не «ответил неверно», и ставить за
  // него ошибку нельзя.
  useEffect(() => {
    if (checkAll > 0 && val.trim() && !done) setDone(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkAll]);
  // ⚠️ Сверка общая с сервером: lib/normalizeAnswer.ts ↔ shared/textnorm.
  // Своего сравнения здесь быть не должно — сервер ПЕРЕПРОВЕРЯЕТ ответ и
  // заменяет вердикт браузера своим, а расхождение даёт худший случай:
  // галочка на экране и ошибка в базе. Варианты через «/» в ключе — как
  // progress-service slash_variants.go (lessonAnswerMatch).
  const correct = lessonAnswerMatch(val, item.answer ?? '', caseSensitive);
  return (
    <div className="space-y-2">
      {/* ⚠️ КАРТИНКА КОМПАКТНАЯ, А НЕ ВО ВЕСЬ ЭКРАН. Стояло `max-h-64` без
          ограничения по ширине: восемь предметов растягивались на восемь экранов,
          и ученик прокручивал страницу вместо того, чтобы видеть задание целиком.
          Заказчик 07.09.2026 прислал эталон и нашу простыню рядом.

          Квадрат с `object-contain`: предметы приходят разной формы (яблоко почти
          квадратное, стол вытянутый), и без общей рамки ряд получался рваным. */}
      {item.image_url && (
        <Watermark>
          <img
            src={item.image_url}
            alt=""
            className="aspect-square w-full rounded-card object-contain"
            draggable={false}
          />
        </Watermark>
      )}
      {/*
        ⚠️ КНОПКИ «ПРОВЕРИТЬ» У КАЖДОЙ КАРТИНКИ НЕТ — она одна на весь блок,
        внизу. Восемь предметов давали восемь кнопок.

        ⚠️ И ОТВЕТ НЕ ЗАСЧИТЫВАЕТСЯ ПО УХОДУ ИЗ ПОЛЯ. Первая редакция этой
        правки так и делала — и это было хуже кнопок: человек пишет половину
        слова, отвлекается на другое поле, и недописанное уходит в статистику
        как ошибка. Кнопка внизу — это ЕГО решение «я закончил», и отменить
        случайный клик мышью проще, чем неверную оценку.

        Enter оставлен: он привычен и означает ровно то же — «этот ответ готов».
      */}
      <input
        value={val}
        onChange={(e) => { setRestored(false); setVal(e.target.value); }}
        onKeyDown={(e) => { if (e.key === 'Enter' && val.trim() && !done) setDone(true); }}
        disabled={done}
        className="w-full rounded-xl border-2 border-info/25 bg-surface-container-lowest px-3.5 py-2 text-[13.5px] text-on-surface outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20"
        placeholder={t('blockRenderers.inputPlaceholder')}
      />
      {done && (
        <RetryResult correct={correct} answer={item.answer} answerData={val} silent={restored} onRetry={() => { setRestored(false); setVal(''); setDone(false); }} onAnswer={(p, silent) => report(index, p.is_correct, p.answer_data, !silent)} />
      )}
    </div>
  );
};

const WordInputImageBlock: Renderer = ({ block, onAnswer, saved }) => {
  const { t } = useTranslation();
  const c = block.content as { instruction?: string };
  const items = useMemo(() => normalizeImageItems(block.content), [block.content]);
  const report = useBlockResult(items.length, items.length === 1, onAnswer);
  const initials = useMemo(() => savedItemValues(saved, items.length === 1), [saved, items.length]);

  /**
   * ⚠️ СЕТКА, А НЕ СТОЛБИК. Каждый предмет занимал целый экран, и задание из
   * восьми картинок превращалось в восемь экранов прокрутки: ученик не видел
   * задание целиком и не понимал, сколько ещё осталось.
   *
   * ⚠️ ЧЕТЫРЕ В РЯД ТОЛЬКО НА ШИРОКОМ ЭКРАНЕ. На телефоне — два: восемь из
   * десяти учеников заходят с телефона, и четыре картинки в ряд там были бы
   * размером с ноготь.
   *
   * ⚠️ НУМЕРАЦИЯ «1 / 8» УБРАНА ВМЕСТЕ С MultiItem. В столбике она подсказывала,
   * где ты; в сетке всё видно сразу, а подпись над каждой клеткой только шумит.
   */
  /**
   * ⚠️ ОДНА КНОПКА НА ВЕСЬ БЛОК, А НЕ ВОСЕМЬ.
   *
   * Первая редакция засчитывала ответ по уходу из поля — и это оказалось хуже
   * восьми кнопок: человек пишет половину слова, отвлекается на соседнее поле,
   * и недописанное уходит в статистику как ошибка. Нажатие внизу — это ЕГО
   * решение «я закончил», и случайный клик мышью отменить проще, чем неверную
   * оценку в отчёте.
   */
  const [checkAll, setCheckAll] = useState(0);

  return (
    <div className="space-y-4">
      <Instruction text={c.instruction} />
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
        {items.map((it, i) => (
          <WordInputImageItem key={i} item={it} index={i} report={report} initial={initials[i]} checkAll={checkAll} caseSensitive={caseSensitiveOf(block.content)} />
        ))}
      </div>
      <Button onClick={() => setCheckAll((n) => n + 1)}>{t('blockRenderers.check')}</Button>
    </div>
  );
};

// One "choose the correct word form" prompt (word + options). Reuses ImageItem's
// {word, options, answer} fields so a single normalize covers both families.
const SelectFormItem = ({
  item,
  index,
  report,
  initial,
}: {
  item: ImageItem;
  index: number;
  report: ItemReport;
  initial?: string;
}) => {
  const { t } = useTranslation();
  const [picked, setPicked] = useState<string | null>(initial ?? null);
  const correct = picked === item.answer;
  // Восстановленный выбор — сборщику блока, без отправки (см. useBlockResult).
  useEffect(() => {
    if (initial != null) report(index, initial === item.answer, initial, false);
    // только при появлении на экране
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div className="space-y-3">
      {item.word && <p className="text-center text-lg font-semibold text-on-surface">{item.word}</p>}
      <div className="flex flex-wrap justify-center gap-2.5">
        {(item.options ?? []).map((opt, oi) => (
          <button
            key={oi}
            disabled={picked !== null}
            onClick={() => {
              setPicked(opt);
              report(index, opt === item.answer, opt);
            }}
            className={cn(
              'ex-pill',
              picked === opt && correct && 'ex-pill--ok',
              picked === opt && !correct && 'ex-pill--bad',
              picked !== null && picked !== opt && opt === item.answer && 'ex-pill--ok',
              picked !== null && picked !== opt && 'opacity-60',
            )}
          >
            {opt}
          </button>
        ))}
      </div>
      {picked !== null && !correct && (
        <div className="flex items-center justify-between gap-2">
          <p className="text-sm text-error">{t('blockRenderers.correctAnswer', { answer: item.answer })}</p>
          <Button size="sm" variant="ghost" onClick={() => setPicked(null)}>
            <span className="material-symbols-outlined text-base">refresh</span>
            {t('blockRenderers.tryAgain')}
          </Button>
        </div>
      )}
    </div>
  );
};

const SelectFormBlock: Renderer = ({ block, onAnswer, saved }) => {
  const c = block.content as { instruction?: string };
  const items = useMemo(() => normalizeImageItems(block.content), [block.content]);
  const report = useBlockResult(items.length, items.length === 1, onAnswer);
  const initials = useMemo(() => savedItemValues(saved, items.length === 1), [saved, items.length]);
  return (
    <div className="space-y-4">
      <Instruction text={c.instruction} />
      {items.map((it, i) => (
        <MultiItem key={i} index={i} total={items.length}>
          <SelectFormItem item={it} index={i} report={report} initial={initials[i]} />
        </MultiItem>
      ))}
    </div>
  );
};

// ---- Text ----

// Text blocks are authored in a plain textarea, so a blank line between
// paragraphs is just a "\n\n" that HTML collapses into a single space — the
// text ran together. textToHtml turns blank lines into real <p> paragraphs and
// single newlines into <br>, while leaving genuine HTML (legacy rich content)
// untouched so it still renders as before.
const escapeHtml = (s: string) =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

function textToHtml(raw?: string | null): string {
  if (!raw) return '';
  if (/<(p|div|br|ul|ol|h[1-6]|blockquote|table|img|a|b|i|u|s|strong|em|span|mark|font)[\s>/]/i.test(raw)) return raw;
  return raw
    .split(/\n{2,}/)
    .map((para) => para.trim())
    .filter(Boolean)
    .map((para) => `<p>${escapeHtml(para).replace(/\n/g, '<br>')}</p>`)
    .join('');
}

const TextBlock: Renderer = ({ block }) => {
  const c = block.content as { html?: string };
  return (
    <div
      className="rt prose prose-on-surface max-w-none leading-relaxed text-on-surface"
      dangerouslySetInnerHTML={{ __html: sanitizeHtml(textToHtml(c.html)) }}
    />
  );
};

// Edvibe article = optional image + heading + body text. Falls back to the
// legacy {html} shape so old article blocks still render.
const ArticleBlock: Renderer = ({ block }) => {
  const c = block.content as { image_url?: string; heading?: string; body?: string; html?: string };
  const structured = c.heading || c.body || c.image_url;
  if (!structured && c.html) {
    return (
      <div
        className="rt prose prose-on-surface max-w-none leading-relaxed text-on-surface"
        dangerouslySetInnerHTML={{ __html: sanitizeHtml(textToHtml(c.html)) }}
      />
    );
  }
  return (
    <article className="space-y-3">
      {c.image_url && (
        <img src={c.image_url} alt={c.heading ?? ''} className="max-h-96 w-full rounded-card object-cover" />
      )}
      {c.heading && <h3 className="font-display text-xl font-semibold text-on-surface">{c.heading}</h3>}
      <RichText text={c.body} className="text-on-surface" />
    </article>
  );
};

const EssayBlock: Renderer = ({ block, onAnswer, saved: savedAnswer, missing, readOnly }) => {
  const { t } = useTranslation();
  const c = block.content as { prompt?: string; min_words?: number };
  // ⚠️ ЧЕРНОВИК — ТОЛЬКО В ПРИЛОЖЕНИИ (24.09.2026, сборка 61). Там появился свайп назад от края, и уйти из урока
  // посреди сочинения стало легко случайно; решение владельца — сочинению черновик. Ключ — с учёткой: телефон
  // бывает общим, и чужое сочинение не должно всплыть у другого ученика. Черновик сильнее сохранённого ответа:
  // он появляется, только если после сохранения что-то правили. На сайте — как было.
  const userId = useAuthStore((st) => st.user?.id);
  const draftKey = isStoreBuild() && userId ? `lesson-essay:${userId}:${block.id}` : null;
  const [draft] = useState(() => (draftKey ? loadDraft<string>(draftKey) : null));
  const [val, setVal] = useState(draft ?? savedAnswer?.answer_data ?? '');
  const [saved, setSaved] = useState(!!savedAnswer && draft === null);
  /** Ответ лёг в очередь: связи нет, на сервер ещё не ушёл. */
  const [queued, setQueued] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const words = essayWordCount(val);
  // 0 / unset = no minimum from the builder — but at least one word is always
  // required (owner decision 10.09.2026): the server rejects an essay without a
  // single letter or digit (`essay_empty`).
  const min = c.min_words ?? 0;
  const hasMin = min > 0;
  const canSave = essayCanSave(val, min);
  // Пустое сочинение, сохранённое до правила, или названное сервером в 409 — не
  // зелёное «Сохранено», а причина у поля (11.09.2026).
  const savedProblem = saved && !queued ? savedOpenAnswerProblem({ type: 'essay', answer: val, namedByServer: missing }) : null;

  // ⚠️ СОХРАНЕНИЕ ЖДЁТ ОТВЕТА. Раньше «сохранено» ставилось сразу, до сервера:
  // отказ «пустой ответ» и ответ, лёгший в очередь без связи, выглядели одинаково —
  // зелёной галочкой.
  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const delivered = await onAnswer?.({ answer_data: val, is_correct: true });
      setSaved(true);
      setQueued(delivered === false);
      // Ответ ушёл (или лёг в очередь, которая переживает перезапуск) — черновик больше не нужен.
      if (draftKey) clearDraft(draftKey);
    } catch (e) {
      setError(t(openAnswerRejectionKey(e) ?? 'blockRenderers.essaySaveError'));
    } finally {
      setSaving(false);
    }
  };

  // ⚠️ РАЗБОР — ТЕКСТОМ, А НЕ ПОЛЕМ (26.09.2026). Раньше куратор видел то же поле на шесть строк, что
  // и ученик, внутри `.review-inert { pointer-events: none }`: колесо и касание проходили сквозь поле,
  // и сочинение длиннее шести строк прокрутить было нельзя. Весь текст целиком, страница прокручивается
  // как обычно; перенос длинных слов — чтобы строка без пробелов не распирала экран на 390. Показываем
  // сохранённый ответ, а не черновик: черновик — вещь того, кто сидит за экраном, а не ученика.
  if (readOnly) {
    const text = savedAnswer?.answer_data ?? '';
    const answerWords = essayWordCount(text);
    return (
      <div className="space-y-2">
        <RichText text={c.prompt} className="text-on-surface" />
        {text !== '' && (
          <>
            <div
              data-essay-answer
              className="whitespace-pre-wrap break-words rounded-button bg-surface-container-lowest p-3 text-on-surface shadow-soft"
            >
              {text}
            </div>
            <p className="text-sm text-on-surface-variant">
              {hasMin
                ? t('blockRenderers.wordCount', { words: answerWords, min })
                : t('blockRenderers.wordCountNoMin', { count: answerWords })}
            </p>
          </>
        )}
      </div>
    );
  }
  return (
    <div className="space-y-2">
      <RichText text={c.prompt} className="text-on-surface" />
      <textarea
        rows={6}
        value={val}
        // Typing after a save re-enables the button so the student can resubmit.
        onChange={(e) => {
          setVal(e.target.value);
          if (draftKey) saveDraft(draftKey, e.target.value);
          setSaved(false);
          setQueued(false);
          setError(null);
        }}
        className="w-full resize-y rounded-button bg-surface-container-lowest p-3 text-on-surface shadow-soft focus:outline-none focus:ring-2 focus:ring-primary"
        placeholder={t('blockRenderers.essayPlaceholder')}
      />
      <div className="flex items-center justify-between gap-3 text-sm">
        <span className={hasMin && words >= min ? 'text-success' : 'text-on-surface-variant'}>
          {hasMin
            ? t('blockRenderers.wordCount', { words, min })
            : t('blockRenderers.wordCountNoMin', { count: words })}
        </span>
        {saved && !savedProblem ? (
          queued ? (
            <span className="flex items-center gap-1.5 font-medium text-on-surface-variant">
              <span className="material-symbols-outlined text-base">cloud_off</span>
              {t('blockView.queued')}
            </span>
          ) : (
            <span className="flex items-center gap-1.5 font-medium text-success">
              <span className="material-symbols-outlined text-base">check_circle</span>
              {t('blockRenderers.saved')}
            </span>
          )
        ) : (
          <Button size="sm" disabled={!canSave} loading={saving} onClick={save}>
            {t('common.save')}
          </Button>
        )}
      </div>
      {savedProblem ? (
        <p role="alert" className="text-sm font-medium text-error">
          {t('blockRenderers.essayMinOneWord')}
        </p>
      ) : (
        !saved &&
        !essayCanSave(val, 0) && <p className="text-sm text-on-surface-variant">{t('blockRenderers.essayMinOneWord')}</p>
      )}
      {error && <p className="text-sm text-error">{error}</p>}
    </div>
  );
};

// ---- «Ответ по пунктам» (open_items, 13.10.2026; договор block-types-1.1.2 §8) ----

/** Поле в одну строку, растёт по тексту — как у макета владельца. Высота считается по `scrollHeight`. */
function GrowField({
  value,
  onChange,
  placeholder,
  label,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  label: string;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [value]);
  return (
    <textarea
      ref={ref}
      rows={1}
      value={value}
      aria-label={label}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      className="mt-2 block w-full resize-none overflow-hidden rounded-button bg-surface-container-lowest p-3 text-on-surface shadow-soft focus:outline-none focus:ring-2 focus:ring-primary"
    />
  );
}

/**
 * Открытое задание без правильного ответа: ученик пишет по строке на пункт, проверяет куратор.
 *
 * ⚠️ «ГОТОВО» = ЗАПОЛНЕНЫ ВСЕ ПУНКТЫ, а не «верно»: ✓/✗ здесь нет. Правило — `openItemsAnswerOk`, двойник
 * серверного (progress 000027); ответ пишется с `is_correct: true`, как у сочинения, и уходит куратору в сдачу.
 * Образец (`items[].sample`) ученику сервер не отдаёт (courses `СкрытьОбразцыОтУченика`) — его видит куратор.
 */
const OpenItemsBlock: Renderer = ({ block, onAnswer, saved: savedAnswer, missing, readOnly }) => {
  const { t } = useTranslation();
  const c = block.content as OpenItemsContent;
  const items = openItemsOf(c);
  const count = items.length;
  // Черновик — только в приложении, с учёткой в ключе: как у сочинения (свайп назад от края, общий телефон).
  const userId = useAuthStore((st) => st.user?.id);
  const draftKey = isStoreBuild() && userId ? `lesson-items:${userId}:${block.id}` : null;
  const [draft] = useState(() => (draftKey ? loadDraft<string[]>(draftKey) : null));
  const savedVals = savedAnswer ? parseOpenItemsAnswer(savedAnswer.answer_data, count) : null;
  const [vals, setVals] = useState<string[]>(() =>
    Array.isArray(draft) ? parseOpenItemsAnswer(JSON.stringify(draft), count) : (savedVals ?? Array(count).fill('')),
  );
  const [sent, setSent] = useState(!!savedAnswer && draft === null);
  const [queued, setQueued] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Ответ, пришедший позже первой отрисовки (техдолг п. 419), подхватывается, пока ученик сам ничего не ввёл.
  const savedKey = savedAnswer?.answer_data ?? '';
  useEffect(() => {
    if (!savedVals || draft !== null) return;
    if (vals.some((v) => v !== '')) return;
    setVals(savedVals);
    setSent(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedKey]);

  const filled = openItemsFilled(vals);
  const answer = JSON.stringify(vals);
  const canSend = count > 0 && openItemsAnswerOk(c, answer);
  const problem = sent && !queued ? savedOpenAnswerProblem({ type: 'open_items', answer, namedByServer: missing }) : null;
  const sentOk = sent && !problem && openItemsAnswerOk(c, answer);

  const send = async () => {
    setSaving(true);
    setError(null);
    try {
      const delivered = await onAnswer?.({ answer_data: answer, is_correct: true });
      setSent(true);
      setQueued(delivered === false);
      if (draftKey) clearDraft(draftKey);
    } catch (e) {
      setError(t(openAnswerRejectionKey(e) ?? 'blockRenderers.essaySaveError'));
    } finally {
      setSaving(false);
    }
  };

  const change = (i: number, v: string) => {
    const next = vals.slice();
    next[i] = v;
    setVals(next);
    if (draftKey) saveDraft(draftKey, next);
    setSent(false);
    setQueued(false);
    setError(null);
  };

  const head = (it: OpenItem, i: number) => (
    <>
      <span className="block text-on-surface">
        <span className="mr-1 font-bold">{i + 1}.</span>
        {it.text}
      </span>
      {it.hint && <p className="mt-0.5 text-sm text-on-surface-variant">{it.hint}</p>}
    </>
  );

  // Разбор у куратора: ответы текстом (выделяются — data-essay-answer, как у сочинения) и образец под ними.
  if (readOnly) {
    const answered = savedAnswer ? parseOpenItemsAnswer(savedAnswer.answer_data, count) : [];
    return (
      <div className="space-y-3">
        <RichText text={c.instruction} className="font-medium text-on-surface" />
        <ol className="list-none p-0">
          {items.map((it, i) => (
            <li key={i} className={cn(i > 0 && 'mt-3 border-t border-outline-variant/60 pt-3')}>
              {head(it, i)}
              {answered[i]?.trim() ? (
                <div
                  data-essay-answer
                  className="mt-2 whitespace-pre-wrap break-words rounded-button bg-surface-container-lowest p-3 text-on-surface shadow-soft"
                >
                  {answered[i]}
                </div>
              ) : (
                <p className="mt-2 text-sm text-on-surface-variant">{t('blockReview.unanswered')}</p>
              )}
              {it.sample?.trim() && (
                <p className="mt-1 text-sm text-on-surface-variant">
                  <span className="font-semibold">{t('blockRenderers.openItemsSample')}</span> {it.sample}
                </p>
              )}
            </li>
          ))}
        </ol>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <RichText text={c.instruction} className="font-medium text-on-surface" />
      <ol className="list-none p-0">
        {items.map((it, i) => (
          <li key={i} className={cn(i > 0 && 'mt-3 border-t border-outline-variant/60 pt-3')}>
            {head(it, i)}
            <GrowField
              value={vals[i] ?? ''}
              onChange={(v) => change(i, v)}
              placeholder={t('blockRenderers.openItemsPlaceholder')}
              label={`${i + 1}`}
            />
          </li>
        ))}
      </ol>
      {sentOk ? (
        queued ? (
          <span className="flex items-center gap-1.5 text-sm font-medium text-on-surface-variant">
            <span className="material-symbols-outlined text-base">cloud_off</span>
            {t('blockView.queued')}
          </span>
        ) : (
          <Pill variant="primary">{t('blockRenderers.openItemsOnReview')}</Pill>
        )
      ) : (
        <div>
          <Button fullWidth disabled={!canSend} loading={saving} onClick={send}>
            {t('blockRenderers.openItemsSend')}
          </Button>
          <p className="mt-2 text-center text-[13px] text-on-surface-variant">
            {t('blockRenderers.openItemsFilled', { filled, total: count })}
          </p>
        </div>
      )}
      {problem && (
        <p role="alert" className="text-sm font-medium text-error">
          {t('blockRenderers.openItemsFillAll')}
        </p>
      )}
      {error && <p className="text-sm text-error">{error}</p>}
    </div>
  );
};

// ---- Misc ----

const CalloutBlock: Renderer = ({ block }) => {
  const c = block.content as { level?: string; color?: string; title?: string; text?: string };
  // Edvibe-style tinted callout with a coloured circle icon. Backward-compatible
  // with the old {level} shape (info/tip/warning) and the new {color} palette
  // (green/blue/amber/red/gray) + optional title.
  const palette = ['green', 'blue', 'amber', 'red', 'gray'];
  const variant = c.color && palette.includes(c.color)
    ? c.color
    : c.level === 'warning'
      ? 'amber'
      : c.level === 'tip'
        ? 'green'
        : 'blue';
  // ⚠️ ДЛЯ ЯНТАРНОЙ ЗАМЕТКИ — 'error_outline', А НЕ 'warning'.
  // Значка 'warning' в нашем урезанном шрифте нет, и лигатура не срабатывала:
  // 07.09.2026 заказчик прислал урок, где поверх первой строки текста лежало
  // жёлтым кругом слово «WARNING». Контурный значок отличается от красного
  // залитого 'error' и читается как предупреждение.
  const icon =
    variant === 'amber'
      ? 'error_outline'
      : variant === 'red'
        ? 'error'
        : variant === 'green'
          ? 'lightbulb'
          : 'info';
  return (
    <div className={cn('ex-callout', `ex-callout--${variant}`)}>
      <span className="ex-callout__ic">
        <span className="material-symbols-outlined" style={{ fontSize: '15px' }}>{icon}</span>
      </span>
      {titleText(c.title) && (
        <h4 className="mb-1.5 font-semibold text-on-surface" dangerouslySetInnerHTML={{ __html: titleHtml(c.title) }} />
      )}
      <RichText text={c.text} className="text-sm text-on-surface" />
    </div>
  );
};

const LinkBlock: Renderer = ({ block }) => {
  const c = block.content as { url?: string; label?: string };
  if (!c.url) return null;
  return (
    <a
      href={c.url}
      target="_blank"
      rel="noreferrer"
      className="group flex items-center gap-3 rounded-2xl border-2 border-info/25 bg-surface-container-lowest p-3.5 transition hover:border-primary hover:shadow-ambient"
    >
      <span className="grid h-11 w-11 flex-none place-items-center rounded-xl text-white" style={{ background: 'var(--ex-grad)' }}>
        <span className="material-symbols-outlined">link</span>
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate font-semibold text-on-surface">{c.label ?? c.url}</span>
        <span className="block truncate text-xs text-on-surface-variant">{c.url}</span>
      </span>
      <span className="material-symbols-outlined flex-none text-on-surface-variant transition group-hover:text-primary">open_in_new</span>
    </a>
  );
};

const DividerBlock: Renderer = () => <hr className="my-4 border-outline-variant/40" />;

// Universal iframe embed (Wordwall / LearningApps / Miro / H5P / YouTube-embed).
// Only https URLs are rendered; the iframe is sandboxed.
const EmbedBlock: Renderer = ({ block }) => {
  const c = block.content as { url?: string; height?: number };
  if (!c.url || !/^https:\/\//i.test(c.url.trim())) {
    return <p className="text-sm text-on-surface-variant">—</p>;
  }
  return (
    <div className="overflow-hidden rounded-card">
      <iframe
        src={c.url.trim()}
        title="embed"
        className="w-full"
        style={{ height: `${Number(c.height) > 0 ? Number(c.height) : 400}px` }}
        sandbox="allow-scripts allow-same-origin allow-popups allow-forms allow-presentation"
        allow="fullscreen; microphone; camera"
        allowFullScreen
      />
    </div>
  );
};

const PdfBlock: Renderer = ({ block }) => {
  const c = block.content as { url?: string; title?: string };
  if (!c.url) return <p className="text-sm text-on-surface-variant">—</p>;
  return (
    <div className="space-y-2">
      {/* Заголовок бывает с разметкой (D39): над рамкой — как есть, в подпись рамки
          и ссылки — только текст, иначе там появились бы теги. */}
      {titleText(c.title) && (
        <p className="text-sm font-semibold text-on-surface" dangerouslySetInnerHTML={{ __html: titleHtml(c.title) }} />
      )}
      <iframe
        src={c.url}
        title={titleText(c.title) || 'pdf'}
        className="h-[600px] w-full rounded-card border border-outline-variant/40"
      />
      <a href={c.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm font-medium text-primary">
        <span className="material-symbols-outlined text-base">download</span>
        {titleText(c.title) || 'PDF'}
      </a>
    </div>
  );
};

// Highlight-the-words: correct words are wrapped in [brackets] in the source
// text; the student clicks the words they think are correct, then checks.
const HighlightBlock: Renderer = ({ block, onAnswer, saved }) => {
  const { t } = useTranslation();
  const c = block.content as { text?: string };
  const text = c.text ?? '';
  // Tokens: words (clickable) interleaved with raw separators. We keep the raw
  // string slices so spacing/punctuation renders verbatim.
  const parts = useMemo(() => {
    const arr: { key: number; word: string | null; raw: string; correct: boolean; idx: number }[] = [];
    const re = /\[([^\]]+)\]|([\p{L}\p{N}'’]+)|([^\p{L}\p{N}'’]+)/gu;
    let m: RegExpExecArray | null;
    let key = 0;
    let idx = 0;
    while ((m = re.exec(text)) !== null) {
      if (m[1] !== undefined) arr.push({ key: key++, word: m[1], raw: m[1], correct: true, idx: idx++ });
      else if (m[2] !== undefined) arr.push({ key: key++, word: m[2], raw: m[2], correct: false, idx: idx++ });
      else arr.push({ key: key++, word: null, raw: m[3]!, correct: false, idx: -1 });
    }
    return arr;
  }, [text]);
  const correctIdx = useMemo(() => new Set(parts.filter((p) => p.correct).map((p) => p.idx)), [parts]);
  // Restore: answer_data holds the clicked token indices.
  const savedSel = useMemo<number[] | null>(() => {
    if (!saved) return null;
    try {
      const a = JSON.parse(saved.answer_data);
      if (Array.isArray(a)) return a.map(Number);
    } catch { /* not restorable */ }
    return null;
  }, [saved]);
  const [selected, setSelected] = useState<Set<number>>(() => new Set(savedSel ?? []));
  const [checked, setChecked] = useState(savedSel != null);

  const toggle = (idx: number) => {
    if (checked) return;
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(idx)) n.delete(idx); else n.add(idx);
      return n;
    });
  };
  const check = () => {
    setChecked(true);
    const ok = correctIdx.size === selected.size && [...correctIdx].every((i) => selected.has(i));
    onAnswer?.({ answer_data: JSON.stringify([...selected]), is_correct: ok });
  };

  return (
    <div className="space-y-3">
      <p className="leading-loose">
        {parts.map((p) =>
          p.word === null ? (
            <span key={p.key}>{p.raw}</span>
          ) : (
            <span
              key={p.key}
              onClick={() => toggle(p.idx)}
              // Нажатие на слово — начало ответа (окно «Выйти из урока?» в приложении, lib/lessonAnswerGuard.ts):
              // само слово не кнопка, без отметки уход со свайпом съедал выделение молча (найдено 24.09.2026).
              data-answer=""
              className={cn(
                'cursor-pointer rounded-md px-1.5 py-0.5 transition',
                !checked && selected.has(p.idx) && 'bg-[#FFE08A] text-[#5A4200]',
                !checked && !selected.has(p.idx) && 'hover:bg-surface-container-high',
                checked && correctIdx.has(p.idx) && 'bg-success-container text-on-success-container',
                checked && selected.has(p.idx) && !correctIdx.has(p.idx) && 'bg-error-container text-on-error-container line-through',
              )}
            >
              {p.word}
            </span>
          ),
        )}
      </p>
      {!checked && (
        <Button size="sm" onClick={check} disabled={selected.size === 0}>
          {t('blockRenderers.check')}
        </Button>
      )}
    </div>
  );
};

// =============================================================
// Media — extra
// =============================================================

const ImageCarouselBlock: Renderer = ({ block }) => {
  const c = block.content as { images?: string[] };
  const imgs = c.images ?? [];
  const [idx, setIdx] = useState(0);
  if (imgs.length === 0) return null;
  const cur = imgs[Math.min(idx, imgs.length - 1)];
  return (
    <div>
      <Watermark>
        <img
          src={cur}
          alt=""
          className="w-full rounded-card"
          onContextMenu={(e) => e.preventDefault()}
          draggable={false}
        />
      </Watermark>
      {imgs.length > 1 && (
        <div className="mt-2 flex items-center justify-between">
          <Button variant="ghost" onClick={() => setIdx((i) => Math.max(0, i - 1))} disabled={idx === 0}>
            <span className="material-symbols-outlined">chevron_left</span>
          </Button>
          <span className="text-xs text-on-surface-variant">{idx + 1} / {imgs.length}</span>
          <Button
            variant="ghost"
            onClick={() => setIdx((i) => Math.min(imgs.length - 1, i + 1))}
            disabled={idx >= imgs.length - 1}
          >
            <span className="material-symbols-outlined">chevron_right</span>
          </Button>
        </div>
      )}
    </div>
  );
};

const GifBlock: Renderer = ({ block }) => {
  const c = block.content as { url?: string };
  if (!c.url) return null;
  return (
    <Watermark>
      <img
        src={c.url}
        alt=""
        className="w-full rounded-card"
        onContextMenu={(e) => e.preventDefault()}
        draggable={false}
      />
    </Watermark>
  );
};

// =============================================================
// Word fill-in / drag — extra
// =============================================================

// ---- Native drag-and-drop primitives (no external lib) ----
// Payload travels as a plain string via dataTransfer; that is the reliable
// cross-browser channel. A draggable chip is ALSO clickable so touch users
// (where HTML5 DnD is unavailable) still have a path.

type DragChipProps = {
  value: string;
  /** Что уходит при перетаскивании. По умолчанию само слово; раскладка по местам шлёт метку (lib/tapPlace.ts). */
  payload?: string;
  onClick?: (e: ReactMouseEvent) => void;
  disabled?: boolean;
  selected?: boolean;
  className?: string;
};

const DragChip = ({ value, payload, onClick, disabled, selected, className }: DragChipProps) => (
  <button
    type="button"
    draggable={!disabled}
    onDragStart={(e) => e.dataTransfer.setData('text/plain', payload ?? value)}
    onClick={onClick}
    disabled={disabled}
    aria-pressed={selected || undefined}
    className={cn(
      'ex-word',
      selected && 'border-primary bg-primary-container ring-2 ring-primary',
      disabled && 'ex-word--used',
      className,
    )}
  >
    <span className="ex-word__grip">⋮</span>
    {value}
  </button>
);

// A drop zone that reads the text/plain payload. Highlights while a chip hovers.
function useDropZone(onDrop: (value: string) => void, disabled?: boolean) {
  const [over, setOver] = useState(false);
  return {
    over,
    props: {
      onDragOver: (e: DragEvent) => {
        if (disabled) return;
        e.preventDefault();
        setOver(true);
      },
      onDragLeave: () => setOver(false),
      onDrop: (e: DragEvent) => {
        e.preventDefault();
        setOver(false);
        if (disabled) return;
        const v = e.dataTransfer.getData('text/plain');
        if (v) onDrop(v);
      },
    },
  };
}

// One or many drag-a-word items per block (to a sentence gap OR onto a picture).
type DragItem = { sentence?: string; image_url?: string; options?: string[]; answer?: string };
function normalizeDragItems(content: unknown): DragItem[] {
  const c = (content ?? {}) as { items?: DragItem[]; sentence?: string; image_url?: string; options?: string[]; answer?: string };
  if (Array.isArray(c.items) && c.items.length > 0) {
    return c.items.map((it) => ({ sentence: it?.sentence, image_url: it?.image_url, options: it?.options, answer: it?.answer }));
  }
  return [{ sentence: c.sentence, image_url: c.image_url, options: c.options, answer: c.answer }];
}

const DragToGapItem = ({
  item,
  index,
  report,
  initial,
}: {
  item: DragItem;
  index: number;
  report: ItemReport;
  initial?: string;
}) => {
  const { t } = useTranslation();
  const [picked, setPicked] = useState<string | null>(initial ?? null);
  const [done, setDone] = useState(initial != null);
  const [restored, setRestored] = useState(initial != null);
  const parts = (item.sentence ?? '').split('___');
  const gap = useDropZone((v) => { if (!done) { setRestored(false); setPicked(v); } }, done);
  const correct = picked === item.answer;
  return (
    <div className="space-y-3">
      <p className="text-on-surface">
        {parts[0]}
        <span
          {...gap.props}
          className={cn(
            'mx-1 inline-block min-w-[80px] rounded-button border-2 border-dashed border-info/40 px-2 py-0.5 text-center',
            gap.over ? 'border-primary bg-primary-container' : 'border-outline',
            done && (correct ? 'border-success text-success' : 'border-error text-error'),
          )}
        >
          {picked ?? '___'}
        </span>
        {parts[1] ?? ''}
      </p>
      <div className="flex flex-wrap gap-2">
        {(item.options ?? []).map((opt, oi) => (
          <DragChip key={oi} value={opt} disabled={done} selected={picked === opt} onClick={() => { if (!done) { setRestored(false); setPicked(opt); } }} />
        ))}
      </div>
      {!done ? (
        <Button size="sm" disabled={!picked} onClick={() => setDone(true)}>
          {t('blockRenderers.check')}
        </Button>
      ) : (
        <RetryResult correct={correct} answer={item.answer} silent={restored} onRetry={() => { setRestored(false); setPicked(null); setDone(false); }} onAnswer={(p, silent) => report(index, p.is_correct, p.answer_data, !silent)} answerData={picked ?? ''} />
      )}
    </div>
  );
};

const DragToGapBlock: Renderer = ({ block, onAnswer, saved }) => {
  const c = block.content as { instruction?: string };
  const items = useMemo(() => normalizeDragItems(block.content), [block.content]);
  const report = useBlockResult(items.length, items.length === 1, onAnswer);
  const initials = useMemo(() => savedItemValues(saved, items.length === 1), [saved, items.length]);
  return (
    <Card className="border-2 border-info/25 space-y-4">
      {c.instruction && <RichText text={c.instruction} className="font-medium text-on-surface" />}
      {items.map((it, i) => (
        <MultiItem key={i} index={i} total={items.length}>
          <DragToGapItem item={it} index={i} report={report} initial={initials[i]} />
        </MultiItem>
      ))}
    </Card>
  );
};

const DragToImageItem = ({
  item,
  index,
  report,
  initial,
}: {
  item: DragItem;
  index: number;
  report: ItemReport;
  initial?: string;
}) => {
  const { t } = useTranslation();
  const [picked, setPicked] = useState<string | null>(initial ?? null);
  const [done, setDone] = useState(initial != null);
  const [restored, setRestored] = useState(initial != null);
  const drop = useDropZone((v) => { if (!done) { setRestored(false); setPicked(v); } }, done);
  const correct = picked === item.answer;
  return (
    <div className="space-y-3">
      {item.image_url && (
        <Watermark>
          <div {...drop.props} className={cn('relative mx-auto w-fit rounded-card', drop.over && 'ring-2 ring-primary')}>
            <img src={item.image_url} alt="" className="mx-auto max-h-64 rounded-card" draggable={false} />
            {picked && (
              <span className={cn(
                'absolute bottom-2 left-1/2 -translate-x-1/2 rounded-pill px-3 py-1 text-sm shadow-ambient',
                done ? (correct ? 'bg-success text-on-success' : 'bg-error text-on-error') : 'bg-primary text-on-primary',
              )}>
                {picked}
              </span>
            )}
          </div>
        </Watermark>
      )}
      <div className="flex flex-wrap justify-center gap-2">
        {(item.options ?? []).map((opt, oi) => (
          <DragChip key={oi} value={opt} disabled={done} selected={picked === opt} onClick={() => { if (!done) { setRestored(false); setPicked(opt); } }} />
        ))}
      </div>
      {!done ? (
        <Button size="sm" disabled={!picked} onClick={() => setDone(true)}>
          {t('blockRenderers.check')}
        </Button>
      ) : (
        <RetryResult correct={correct} answer={item.answer} silent={restored} onRetry={() => { setRestored(false); setPicked(null); setDone(false); }} onAnswer={(p, silent) => report(index, p.is_correct, p.answer_data, !silent)} answerData={picked ?? ''} />
      )}
    </div>
  );
};

const DragToImageBlock: Renderer = ({ block, onAnswer, saved }) => {
  const c = block.content as { instruction?: string };
  const items = useMemo(() => normalizeDragItems(block.content), [block.content]);
  const report = useBlockResult(items.length, items.length === 1, onAnswer);
  const initials = useMemo(() => savedItemValues(saved, items.length === 1), [saved, items.length]);
  return (
    <Card className="border-2 border-info/25 space-y-4">
      {c.instruction && <RichText text={c.instruction} className="font-medium text-on-surface" />}
      {items.map((it, i) => (
        <MultiItem key={i} index={i} total={items.length}>
          <DragToImageItem item={it} index={i} report={report} initial={initials[i]} />
        </MultiItem>
      ))}
    </Card>
  );
};

// Shared post-answer row: shows correct/incorrect, the right answer when wrong,
// a "Try again" reset, and reports the result to progress via onAnswer once.
function RetryResult({
  correct,
  answer,
  answerData,
  onRetry,
  onAnswer,
  silent,
}: {
  correct: boolean;
  answer?: string;
  answerData: string;
  onRetry: () => void;
  onAnswer?: (p: { answer_data: string; is_correct: boolean }, silent: boolean) => void;
  // When restoring a saved answer we must NOT re-report it to the server on
  // mount (it's already saved) — that would spam POSTs on every lesson open.
  // ⚠️ Но сборщику блока о нём сообщаем (`silent = true`): иначе повтор
  // соседнего пункта после перезагрузки не уходит на сервер вовсе.
  silent?: boolean;
}) {
  const { t } = useTranslation();
  useEffect(() => {
    onAnswer?.({ answer_data: answerData, is_correct: correct }, !!silent);
    // report once when the result row mounts
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    // ⚠️ `flex-wrap` НУЖЕН УЗКИМ КЛЕТКАМ. С 07.09.2026 задания с картинками
    // рисуются сеткой, и клетка вдвое-вчетверо уже строки: «Правильный ответ:
    // apple» и кнопка «Ещё раз» в одну строку там не помещались и вылезали за
    // край. В широких блоках перенос ничего не меняет — места хватает.
    <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1">
      <p className={cn('text-[13px]', correct ? 'text-success' : 'text-error')}>
        {correct
          ? t('blockRenderers.correct')
          : answer
            ? t('blockRenderers.correctAnswer', { answer })
            : t('blockRenderers.incorrectRetry')}
      </p>
      {!correct && (
        <Button size="sm" variant="ghost" onClick={onRetry}>
          <span className="material-symbols-outlined text-base">refresh</span>
          {t('blockRenderers.tryAgain')}
        </Button>
      )}
    </div>
  );
}

// =============================================================
// Ordering / sorting
// =============================================================

// One or many "build the sentence from words" items per block.
type SentenceItem = { words?: string[] };
function normalizeSentenceItems(content: unknown): SentenceItem[] {
  const c = (content ?? {}) as { sentences?: SentenceItem[]; words?: string[] };
  if (Array.isArray(c.sentences) && c.sentences.length > 0) {
    return c.sentences.map((s) => ({ words: s?.words ?? [] }));
  }
  return [{ words: c.words ?? [] }];
}

const SentenceOrderItem = ({
  words,
  index,
  report,
  initial,
}: {
  words: string[];
  index: number;
  report: ItemReport;
  initial?: string;
}) => {
  const { t } = useTranslation();
  // Restore: answer_data is the arranged sentence — split it back into the
  // picked words and leave the rest in the pool.
  const restoredPicked = useMemo<string[] | null>(
    () => (initial == null ? null : initial.length ? initial.split(' ') : []),
    [initial],
  );
  // Shuffled copy is computed once via useState init so re-renders don't reshuffle.
  const [pool, setPool] = useState<string[]>(() => {
    if (!restoredPicked) return shuffle(words);
    const rem = [...words];
    for (const w of restoredPicked) {
      const i = rem.indexOf(w);
      if (i >= 0) rem.splice(i, 1);
    }
    return rem;
  });
  const [picked, setPicked] = useState<string[]>(restoredPicked ?? []);
  const [checked, setChecked] = useState(restoredPicked != null);
  const expected = words.join(' ');
  const isCorrect = picked.join(' ') === expected;
  // Восстановленное предложение — сборщику блока, без отправки (см. useBlockResult).
  useEffect(() => {
    if (restoredPicked) report(index, isCorrect, picked.join(' '), false);
    // только при появлении на экране
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const take = (w: string, i: number) => {
    if (checked) return;
    setPicked((p) => [...p, w]);
    setPool((p) => p.filter((_, j) => j !== i));
  };
  const returnWord = (w: string, i: number) => {
    if (checked) return;
    setPicked((p) => p.filter((_, j) => j !== i));
    setPool((p) => [...p, w]);
  };
  const submit = () => {
    if (picked.length === 0) return;
    setChecked(true);
    report(index, isCorrect, picked.join(' '));
  };
  const retry = () => {
    setChecked(false);
    setPool(shuffle(words));
    setPicked([]);
  };
  // Drag a pool word onto the answer line; drag a picked word back to the pool.
  const answerZone = useDropZone((w) => {
    const idx = pool.indexOf(w);
    if (idx >= 0) take(w, idx);
  });
  const poolZone = useDropZone((w) => {
    const idx = picked.indexOf(w);
    if (idx >= 0) returnWord(w, idx);
  });
  return (
    <div className="space-y-3">
      <div
        {...answerZone.props}
        className={cn('ex-bank min-h-[52px]', answerZone.over && 'ring-2 ring-primary/40')}
      >
        {picked.length === 0 ? (
          <span className="self-center text-on-surface-variant">{t('blockRenderers.buildSentence')}</span>
        ) : (
          picked.map((w, i) => (
            <button
              key={`${w}-${i}`}
              type="button"
              draggable
              onDragStart={(e) => e.dataTransfer.setData('text/plain', w)}
              onClick={() => returnWord(w, i)}
              className="ex-word"
            >
              <span className="ex-word__grip">⋮</span>
              {w}
            </button>
          ))
        )}
      </div>
      {!checked && (
        <div {...poolZone.props} className={cn('ex-bank', poolZone.over && 'ring-2 ring-primary/40')}>
          {pool.map((w, i) => (
            <DragChip key={`${w}-${i}`} value={w} onClick={() => take(w, i)} />
          ))}
        </div>
      )}
      {!checked ? (
        <Button size="sm" disabled={picked.length === 0} onClick={submit}>
          {t('blockRenderers.check')}
        </Button>
      ) : (
        <div className="space-y-1">
          <div className="flex items-center justify-between gap-2">
            <p className={isCorrect ? 'text-success' : 'text-error'}>
              {isCorrect ? t('blockRenderers.correct') : t('blockRenderers.incorrectRetry')}
            </p>
            {!isCorrect && (
              <Button size="sm" variant="ghost" onClick={retry}>
                <span className="material-symbols-outlined text-base">refresh</span>
                {t('blockRenderers.tryAgain')}
              </Button>
            )}
          </div>
          {!isCorrect && <p className="text-sm text-on-surface-variant">{expected}</p>}
        </div>
      )}
    </div>
  );
};

const SentenceOrderBlock: Renderer = ({ block, onAnswer, saved }) => {
  const c = block.content as { instruction?: string };
  const items = useMemo(() => normalizeSentenceItems(block.content), [block.content]);
  const report = useBlockResult(items.length, items.length === 1, onAnswer);
  const initials = useMemo(() => savedItemValues(saved, items.length === 1), [saved, items.length]);
  return (
    <Card className="border-2 border-info/25 space-y-4">
      {c.instruction && <RichText text={c.instruction} className="font-medium text-on-surface" />}
      {items.map((it, i) => (
        <MultiItem key={i} index={i} total={items.length}>
          <SentenceOrderItem words={it.words ?? []} index={i} report={report} initial={initials[i]} />
        </MultiItem>
      ))}
    </Card>
  );
};

// One drop-zone column for ColumnSortBlock (own hover state per column).
//
// ⚠️ КОЛОНКА ПРИНИМАЕТ СЛОВО И КАСАНИЕМ: выделил слово — коснулся колонки. До
// 10.09.2026 касание слова в банке не делало ничего, и на телефоне, где
// перетаскивания нет, задание не решалось вовсе.
const SortColumnZone = ({
  label,
  values,
  selected,
  onDropValue,
  onTapZone,
  onTapItem,
  dragPayload,
  statusOf,
  locked,
}: {
  label: string;
  /** Слова в колонке: номер в задании и текст. Номер — потому что текст бывает одинаковым. */
  values: { index: number; value: string }[];
  selected: number | null;
  onDropValue: (v: string) => void;
  onTapZone: () => void;
  onTapItem: (index: number) => void;
  dragPayload: (index: number) => string;
  // After "Проверить" — colour each chip by whether it landed in the right column.
  statusOf?: (index: number) => 'correct' | 'wrong';
  locked?: boolean;
}) => {
  const zone = useDropZone(onDropValue, locked);
  return (
    <div
      {...(locked ? {} : zone.props)}
      onClick={locked ? undefined : onTapZone}
      className={cn(
        'min-h-[130px] rounded-2xl border-2 p-4',
        !locked && zone.over ? 'border-primary bg-primary-container/30' : 'border-info/30',
        !locked && selected !== null && 'border-dashed border-primary',
      )}
    >
      <p className="mb-3 text-center text-sm font-bold text-on-surface">{label}</p>
      <ul className="space-y-2">
        {values.map(({ index, value }) => {
          const s = statusOf?.(index);
          return (
            <li key={index}>
              <button
                type="button"
                draggable={!locked}
                onDragStart={(e) => e.dataTransfer.setData('text/plain', dragPayload(index))}
                onClick={locked ? undefined : (e) => { e.stopPropagation(); onTapItem(index); }}
                aria-pressed={selected === index || undefined}
                className={cn(
                  'ex-word w-full justify-start',
                  selected === index && 'border-primary bg-primary-container ring-2 ring-primary',
                  s === 'correct' && 'border-success bg-success-container text-on-success-container',
                  s === 'wrong' && 'border-error bg-error-container text-on-error-container line-through',
                )}
              >
                <span className="ex-word__grip">⋮</span>
                {value}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

const ColumnSortBlock: Renderer = ({ block, onAnswer, saved }) => {
  const { t } = useTranslation();
  const c = block.content as { instruction?: string; columns?: { label: string; items: string[] }[] };
  const cols = c.columns ?? [];
  // Повтор слова (два «berry») — с невидимым хвостом (lib/matching.ts distinctWords, 13.10.2026):
  // ответ — словарь «слово → колонка», без хвоста второй «berry» затирал первый.
  const flat = cols.flatMap((col, ci) => col.items.map((it) => ({ it, ci })));
  const labels = distinctWords(flat.map((f) => f.it));
  const allItems = flat.map((f, i) => ({ value: labels[i], correctCol: f.ci }));
  // Restore: answer_data holds the value→column assignments.
  const savedAssign = useMemo<Record<string, number | null> | null>(() => {
    if (!saved) return null;
    try {
      const a = JSON.parse(saved.answer_data);
      if (a && typeof a === 'object') return a as Record<string, number | null>;
    } catch { /* not restorable */ }
    return null;
  }, [saved]);
  // ⚠️ Раскладка по НОМЕРАМ слов, а не по тексту: в заданиях встречается одно
  // слово дважды («berry»), и по тексту два таких слова переезжали бы вместе.
  const scope = useId();
  const [placement, setPlacement] = useState<Placement>(() => ({
    at: allItems.map((it) => (savedAssign ? savedAssign[it.value] ?? null : null)),
    sel: null,
  }));
  const [checked, setChecked] = useState(savedAssign != null);
  const dropOn = (place: number | null, token: string) => {
    if (checked) return;
    const item = readDragToken(scope, token);
    if (item !== null) setPlacement((s) => movePlaced(s, item, place, Infinity));
  };
  const poolZone = useDropZone((v) => dropOn(null, v), checked);
  const allAssigned = allItems.length > 0 && allItems.every((_, i) => placement.at[i] != null);
  const allCorrect = allItems.every((it, i) => placement.at[i] === it.correctCol);
  const unassigned = itemsAt(placement, null);
  const submit = () => {
    if (!allAssigned) return;
    setChecked(true);
    setPlacement((s) => ({ ...s, sel: null }));
    // Формат ответа прежний — «слово → колонка»: его читают восстановление и разбор куратора.
    const assignments = Object.fromEntries(allItems.map((it, i) => [it.value, placement.at[i] ?? null]));
    onAnswer?.({ answer_data: JSON.stringify(assignments), is_correct: allCorrect });
  };
  const retry = () => {
    setChecked(false);
    setPlacement(emptyPlacement(allItems.length));
  };
  return (
    <Card className="border-2 border-info/25 space-y-3">
      {c.instruction && <RichText text={c.instruction} className="font-medium text-on-surface" />}
      <div className="grid gap-2 md:grid-cols-2">
        {cols.map((col, ci) => (
          <SortColumnZone
            key={ci}
            label={col.label}
            values={itemsAt(placement, ci).map((i) => ({ index: i, value: allItems[i].value }))}
            selected={checked ? null : placement.sel}
            statusOf={checked ? (i) => (allItems[i].correctCol === ci ? 'correct' : 'wrong') : undefined}
            locked={checked}
            onDropValue={(v) => dropOn(ci, v)}
            onTapZone={() => setPlacement((s) => tapPlace(s, ci, Infinity))}
            onTapItem={(i) => setPlacement((s) => tapItem(s, i))}
            dragPayload={(i) => dragToken(scope, i)}
          />
        ))}
      </div>
      {!checked && (
        <div
          {...poolZone.props}
          onClick={() => setPlacement((s) => tapBank(s))}
          className={cn('flex min-h-[44px] flex-wrap gap-2 rounded-button p-1', poolZone.over && 'bg-surface-container-high')}
        >
          {unassigned.length === 0 ? (
            <span className="px-1 text-sm text-on-surface-variant">{t('blockRenderers.dragToColumns')}</span>
          ) : (
            unassigned.map((i) => (
              <DragChip
                key={i}
                value={allItems[i].value}
                payload={dragToken(scope, i)}
                selected={placement.sel === i}
                onClick={(e) => {
                  e.stopPropagation();
                  setPlacement((s) => tapItem(s, i));
                }}
              />
            ))
          )}
        </div>
      )}
      {!checked ? (
        <Button size="sm" onClick={submit} disabled={!allAssigned}>
          {t('blockRenderers.check')}
        </Button>
      ) : (
        <div className="flex items-center justify-between gap-2">
          <p className={allCorrect ? 'text-success' : 'text-error'}>
            {allCorrect ? t('blockRenderers.correct') : t('blockRenderers.incorrectRetry')}
          </p>
          {!allCorrect && (
            <Button size="sm" variant="ghost" onClick={retry}>
              <span className="material-symbols-outlined text-base">refresh</span>
              {t('blockRenderers.tryAgain')}
            </Button>
          )}
        </div>
      )}
    </Card>
  );
};

const ParagraphOrderBlock: Renderer = ({ block, onAnswer, saved }) => {
  const { t } = useTranslation();
  const c = block.content as { instruction?: string; paragraphs?: string[] };
  const expected = c.paragraphs ?? [];
  // Restore: answer_data holds the arranged order (array of indices).
  const savedOrder = useMemo<number[] | null>(() => {
    if (!saved) return null;
    try {
      const a = JSON.parse(saved.answer_data);
      if (Array.isArray(a) && a.length === expected.length) return a.map(Number);
    } catch { /* not restorable */ }
    return null;
  }, [saved, expected.length]);
  const [order, setOrder] = useState<number[]>(() => savedOrder ?? shuffle(expected.map((_, i) => i)));
  const [dragFrom, setDragFrom] = useState<number | null>(null);
  // Клик-клик: касание абзаца выделяет его, касание другого ставит на то место.
  const [sel, setSel] = useState<number | null>(null);
  const [overIdx, setOverIdx] = useState<number | null>(null);
  const [checked, setChecked] = useState(savedOrder != null);
  const isCorrect = order.every((v, i) => v === i);
  const move = (idx: number, dir: -1 | 1) => {
    if (checked) return;
    const j = idx + dir;
    if (j < 0 || j >= order.length) return;
    const next = [...order];
    [next[idx], next[j]] = [next[j], next[idx]];
    setOrder(next);
  };
  // Drag row `from` and drop it onto row `to` → reinsert at that position.
  const reorder = (from: number, to: number) => {
    if (checked || from === to) return;
    setOrder((prev) => {
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
  };
  const submit = () => {
    setChecked(true);
    onAnswer?.({ answer_data: JSON.stringify(order), is_correct: isCorrect });
  };
  const retry = () => {
    setChecked(false);
    setSel(null);
    setOrder(shuffle(expected.map((_, i) => i)));
  };
  return (
    <Card className="border-2 border-info/25 space-y-2">
      {c.instruction && <RichText text={c.instruction} className="font-medium text-on-surface" />}
      {order.map((origIdx, i) => (
        <div
          key={i}
          draggable={!checked}
          // setData обязателен: без него Firefox перетаскивание не начинает.
          onDragStart={(e) => { e.dataTransfer.setData('text/plain', String(i)); setDragFrom(i); setSel(null); }}
          onClick={() => {
            if (checked) return;
            const r = tapRow(order, sel, i);
            setOrder(r.order);
            setSel(r.sel);
          }}
          onDragOver={(e) => { if (checked) return; e.preventDefault(); setOverIdx(i); }}
          onDragLeave={() => setOverIdx((o) => (o === i ? null : o))}
          onDrop={(e) => { e.preventDefault(); if (dragFrom !== null) reorder(dragFrom, i); setDragFrom(null); setOverIdx(null); }}
          className={cn(
            'flex items-center gap-2.5 rounded-2xl border p-3.5',
            !checked && 'cursor-grab bg-surface-container-lowest active:cursor-grabbing',
            checked && origIdx === i && 'border-success bg-success-container',
            checked && origIdx !== i && 'border-error bg-error-container',
            !checked && (overIdx === i ? 'border-primary bg-primary-container' : 'border-outline-variant'),
            !checked && sel === i && 'border-primary bg-primary-container ring-2 ring-primary',
            !checked && sel !== null && sel !== i && 'border-dashed',
          )}
        >
          <span className="material-symbols-outlined text-base text-on-surface-variant">drag_indicator</span>
          <div className="flex flex-col">
            <button type="button" onClick={(e) => { e.stopPropagation(); setSel(null); move(i, -1); }} disabled={checked || i === 0} className="disabled:opacity-30">
              <span className="material-symbols-outlined text-base">expand_less</span>
            </button>
            <button type="button" onClick={(e) => { e.stopPropagation(); setSel(null); move(i, 1); }} disabled={checked || i === order.length - 1} className="disabled:opacity-30">
              <span className="material-symbols-outlined text-base">expand_more</span>
            </button>
          </div>
          <p className="flex-1 whitespace-pre-wrap text-sm">{expected[origIdx]}</p>
        </div>
      ))}
      {!checked ? (
        <Button size="sm" onClick={submit}>
          {t('blockRenderers.check')}
        </Button>
      ) : (
        <div className="flex items-center justify-between gap-2">
          <p className={isCorrect ? 'text-success' : 'text-error'}>
            {isCorrect ? t('blockRenderers.correct') : t('blockRenderers.incorrectRetry')}
          </p>
          {!isCorrect && (
            <Button size="sm" variant="ghost" onClick={retry}>
              <span className="material-symbols-outlined text-base">refresh</span>
              {t('blockRenderers.tryAgain')}
            </Button>
          )}
        </div>
      )}
    </Card>
  );
};

// One or many "unscramble the word" items per block.
type AnagramItem = { word?: string; clue?: string };
function normalizeAnagramItems(content: unknown): AnagramItem[] {
  const c = (content ?? {}) as { words?: AnagramItem[]; word?: string; clue?: string };
  if (Array.isArray(c.words) && c.words.length > 0) {
    return c.words.map((w) => ({ word: w?.word, clue: w?.clue }));
  }
  return [{ word: c.word, clue: c.clue }];
}

// Имя отличается от типа `AnagramItem` — см. пояснение у `QuizQuestionCard`.
const AnagramCard = ({
  item,
  index,
  report,
  initial,
}: {
  item: AnagramItem;
  index: number;
  report: ItemReport;
  initial?: string;
}) => {
  const { t } = useTranslation();
  const target = item.word ?? '';
  const letters = target.split('');
  // Restore: answer_data is the assembled string — split into picked letters.
  const restoredPicked = useMemo<string[] | null>(
    () => (initial == null ? null : initial.length ? initial.split('') : []),
    [initial],
  );
  const [pool, setPool] = useState<string[]>(() => {
    if (!restoredPicked) return shuffle(letters);
    const rem = [...letters];
    for (const l of restoredPicked) {
      const i = rem.indexOf(l);
      if (i >= 0) rem.splice(i, 1);
    }
    return rem;
  });
  const [picked, setPicked] = useState<string[]>(restoredPicked ?? []);
  const [checked, setChecked] = useState(restoredPicked != null);
  const isCorrect = picked.join('') === target;
  // Восстановленное слово — сборщику блока, без отправки (см. useBlockResult).
  useEffect(() => {
    if (restoredPicked) report(index, isCorrect, picked.join(''), false);
    // только при появлении на экране
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const reset = () => {
    setChecked(false);
    setPool(shuffle(letters));
    setPicked([]);
  };
  const submit = () => {
    if (picked.length === 0) return;
    setChecked(true);
    report(index, isCorrect, picked.join(''));
  };
  return (
    <div className="space-y-3">
      {item.clue && <p className="text-center text-sm text-on-surface-variant">{item.clue}</p>}
      <div
        className={cn(
          'rounded-xl border-2 border-dashed border-info/40 p-3 text-center text-xl font-semibold tracking-[0.3em]',
          checked ? (isCorrect ? 'border-success bg-success-container' : 'border-error bg-error-container') : 'border-outline',
        )}
      >
        {picked.length === 0 ? '—' : picked.join('')}
      </div>
      {!checked && (
        <div className="flex flex-wrap justify-center gap-2">
          {pool.map((l, i) => (
            <button
              key={i}
              type="button"
              onClick={() => {
                setPicked((p) => [...p, l]);
                setPool((p) => p.filter((_, j) => j !== i));
              }}
              className="grid h-10 w-10 place-items-center rounded-xl border border-outline-variant bg-surface-container-lowest font-semibold transition hover:border-primary hover:bg-primary-container/40"
            >
              {l}
            </button>
          ))}
        </div>
      )}
      {checked && !isCorrect && (
        <p className="text-center text-sm text-on-surface-variant">{t('blockRenderers.correctAnswer', { answer: target })}</p>
      )}
      <div className="flex justify-between">
        <Button size="sm" variant="ghost" onClick={reset}>
          {t('blockRenderers.reset')}
        </Button>
        {!checked ? (
          <Button size="sm" disabled={picked.length === 0} onClick={submit}>
            {t('blockRenderers.check')}
          </Button>
        ) : (
          <p className={cn('self-center text-sm', isCorrect ? 'text-success' : 'text-error')}>
            {isCorrect ? t('blockRenderers.correct') : t('blockRenderers.incorrectRetry')}
          </p>
        )}
      </div>
    </div>
  );
};

const AnagramBlock: Renderer = ({ block, onAnswer, saved }) => {
  const c = block.content as { instruction?: string };
  const items = useMemo(() => normalizeAnagramItems(block.content), [block.content]);
  const report = useBlockResult(items.length, items.length === 1, onAnswer);
  const initials = useMemo(() => savedItemValues(saved, items.length === 1), [saved, items.length]);
  return (
    <Card className="border-2 border-info/25 space-y-4">
      <Instruction text={c.instruction} />
      {items.map((it, i) => (
        <MultiItem key={i} index={i} total={items.length}>
          <AnagramCard item={it} index={i} report={report} initial={initials[i]} />
        </MultiItem>
      ))}
    </Card>
  );
};

// Two-column "tap to match" pairing: LEFT items are fixed; RIGHT items are the
// same-shaped column (shuffled). The student taps a left item, then its pair on
// the right (or vice-versa) — matched pairs share a numbered colour badge. No
// drag, no word bank. answer_data stays {left: right} so it restores as before.
/**
 * WordsToPicturesBlock — «Match the words and the pictures».
 *
 * Сверху ОДИН общий банк слов, ниже ряд картинок, под каждой поле. Слово,
 * поставленное под картинку, исчезает из банка; снятое — возвращается.
 *
 * Чем отличается от word_drag_to_image, который уже есть: там каждой картинке
 * даётся СВОЙ список вариантов, и картинки независимы — по сути несколько
 * отдельных заданий с выбором, показанных подряд. Здесь банк общий и каждое
 * слово используется ровно один раз, поэтому ученик может рассуждать методом
 * исключения. Это другое упражнение, а не оформление того же.
 */
const WordsToPicturesBlock: Renderer = ({ block, onAnswer, saved }) => {
  const { t } = useTranslation();
  const c = block.content as {
    instruction?: string;
    items?: { image_url?: string; word?: string }[];
    extra_words?: string[];
  };
  const items = (c.items ?? []).filter((it) => it?.image_url || it?.word);

  // Банк: правильные слова плюс отвлекающие сверх числа картинок. Перемешан
  // один раз при открытии — иначе он прыгал бы после каждого перетаскивания.
  const [bankOrder] = useState<string[]>(() =>
    shuffle([...items.map((it) => it.word ?? ''), ...(c.extra_words ?? [])].filter(Boolean)),
  );

  const savedPicks = useMemo<Record<number, string> | null>(() => {
    if (!saved) return null;
    try {
      const a = JSON.parse(saved.answer_data);
      if (a && typeof a === 'object') return a as Record<number, string>;
    } catch { /* not restorable */ }
    return null;
  }, [saved]);

  // ⚠️ РАСКЛАДКА ПО НОМЕРАМ СЛОВ БАНКА, касание и перетаскивание — через
  // lib/tapPlace.ts. До 10.09.2026 касание слова клало его под ПЕРВУЮ свободную
  // картинку, и на телефоне ученик не мог выбрать, под какую.
  //
  // Слово живёт в ОДНОМ месте: перенос снимает его со старого сам. Иначе одно
  // слово оказалось бы под двумя, и «каждое ровно один раз» перестало бы
  // выполняться.
  const scope = useId();
  const [placement, setPlacement] = useState<Placement>(() => {
    const s = emptyPlacement(bankOrder.length);
    if (!savedPicks) return s;
    const at = [...s.at];
    for (const [k, w] of Object.entries(savedPicks)) {
      const j = bankOrder.findIndex((b, n) => b === w && at[n] === null);
      if (j >= 0) at[j] = Number(k);
    }
    return { at, sel: null };
  });
  const [checked, setChecked] = useState(savedPicks != null);
  const [overIdx, setOverIdx] = useState<number | null>(null);

  const picks: Record<number, string> = {};
  items.forEach((_, i) => {
    const o = occupant(placement, i);
    if (o >= 0) picks[i] = bankOrder[o];
  });
  const dropOn = (place: number | null, token: string) => {
    if (checked) return;
    const item = readDragToken(scope, token);
    if (item !== null) setPlacement((s) => movePlaced(s, item, place, 1));
  };

  const available = itemsAt(placement, null);
  const allPlaced = items.length > 0 && Object.keys(picks).length === items.length;

  const check = () => {
    if (checked || !allPlaced) return;
    setChecked(true);
    const correct = items.reduce((n, it, i) => n + (picks[i] === it.word ? 1 : 0), 0);
    onAnswer?.({
      answer_data: JSON.stringify(picks),
      is_correct: correct === items.length,
    });
  };

  const reset = () => {
    setChecked(false);
    setPlacement(emptyPlacement(bankOrder.length));
  };

  if (items.length === 0) return null;

  return (
    <Card className="border-2 border-info/25 space-y-4">
      {c.instruction && <RichText text={c.instruction} className="font-medium text-on-surface" />}

      {/* Банк слов. Скрывается целиком, когда всё расставлено, — пустая рамка
          на экране только сбивает. */}
      {(available.length > 0 || placement.sel !== null) && (
        // Касание пустого места банка возвращает выделенное слово; сюда же его
        // можно бросить мышью. Пока слово выделено, банк виден и пустым.
        <div
          className="flex min-h-[44px] flex-wrap gap-2 rounded-card bg-surface-container-lowest p-3"
          onClick={() => { if (!checked) setPlacement((s) => tapBank(s)); }}
          onDragOver={(e) => { if (!checked) e.preventDefault(); }}
          onDrop={(e) => { e.preventDefault(); dropOn(null, e.dataTransfer.getData('text/plain')); }}
        >
          {available.map((j) => (
            <button
              key={j}
              type="button"
              draggable={!checked}
              disabled={checked}
              aria-pressed={placement.sel === j || undefined}
              onDragStart={(e) => e.dataTransfer.setData('text/plain', dragToken(scope, j))}
              onClick={(e) => {
                // Касание выделяет слово, касание поля под картинкой кладёт его туда.
                e.stopPropagation();
                if (!checked) setPlacement((s) => tapItem(s, j));
              }}
              className={cn(
                'min-h-[44px] rounded-button border border-outline-variant bg-surface-container-lowest px-3 py-1.5 text-sm transition hover:border-primary disabled:opacity-50',
                placement.sel === j && 'border-primary bg-primary-container ring-2 ring-primary',
              )}
            >
              {bankOrder[j]}
            </button>
          ))}
        </div>
      )}

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        {items.map((it, i) => {
          const val = picks[i] ?? '';
          const right = checked && val === it.word;
          return (
            <div key={i} className="space-y-1.5">
              {it.image_url && (
                <img
                  src={it.image_url}
                  alt=""
                  loading="lazy"
                  draggable={false}
                  onContextMenu={(e) => e.preventDefault()}
                  className="aspect-square w-full rounded-card object-cover"
                />
              )}
              <button
                type="button"
                disabled={checked}
                draggable={!checked && occupant(placement, i) >= 0}
                onDragStart={(e) => e.dataTransfer.setData('text/plain', dragToken(scope, occupant(placement, i)))}
                onClick={() => { if (!checked) setPlacement((s) => tapSlot(s, i)); }}
                onDragOver={(e) => { if (!checked) { e.preventDefault(); setOverIdx(i); } }}
                onDragLeave={() => setOverIdx((o) => (o === i ? null : o))}
                onDrop={(e) => {
                  e.preventDefault();
                  setOverIdx(null);
                  dropOn(i, e.dataTransfer.getData('text/plain'));
                }}
                className={cn(
                  'ex-gap flex min-h-[2.5rem] w-full items-center justify-center px-2 text-sm',
                  (overIdx === i || (placement.sel !== null && placement.sel === occupant(placement, i))) && 'ex-gap--sel',
                  !checked && placement.sel !== null && placement.sel !== occupant(placement, i) && 'ex-gap--target',
                  checked && (right ? 'ex-gap--ok' : 'ex-gap--bad'),
                )}
              >
                {val || '—'}
              </button>
              {/* Верный ответ показывается только после проверки и только там,
                  где ученик ошибся: иначе разбор превращается в подсказку. */}
              {checked && !right && (
                <p className="text-center text-xs text-on-surface-variant">{it.word}</p>
              )}
            </div>
          );
        })}
      </div>

      <div className="flex items-center gap-2">
        {!checked ? (
          <Button size="sm" disabled={!allPlaced} onClick={check}>
            {t('blockRenderers.check')}
          </Button>
        ) : (
          <Button size="sm" variant="ghost" onClick={reset}>
            {t('blockRenderers.retry')}
          </Button>
        )}
        <span className="text-xs text-on-surface-variant">
          {Object.keys(picks).length} / {items.length}
        </span>
      </div>
    </Card>
  );
};

const MatchingBlock: Renderer = ({ block, onAnswer, saved }) => {
  const { t } = useTranslation();
  const c = block.content as { instruction?: string; pairs?: { left: string; right: string }[] };
  // Стабильная ссылка: от pairs зависят эффект перемешивания и таблицы соответствий,
  // а `?? []` возвращает новый массив на каждый рендер.
  // ⚠️ Повторы слов («hold → out», «hold → back») — различимые невидимым хвостом,
  // иначе после первой пары из банка исчезали оба «hold» (lib/matching.ts, 13.10.2026).
  const pairs = useMemo(() => distinctPairs(c.pairs ?? []), [c.pairs]);
  // Перемешиваются ОБЕ колонки. Левая раньше шла ровно в порядке из базы, а составляют
  // такие задания обычно по алфавиту или парами подряд — расположение само подсказывало
  // ответ. Правая вдобавок не пересобиралась по «Ещё раз», и на второй попытке ученик
  // просто помнил позиции.
  const deal = () => ({ lefts: shuffle(pairs), rights: shuffle(pairs.map((p) => p.right)) });
  const [order, setOrder] = useState(deal);
  const { lefts, rights } = order;
  // Содержимое блока может приехать после первого рендера — тогда раздача была по
  // пустому списку. Пересдаём только при несовпадении длин, чтобы не тасовать
  // карточки под рукой у ученика.
  useEffect(() => {
    setOrder((prev) =>
      prev.lefts.length === pairs.length ? prev : { lefts: shuffle(pairs), rights: shuffle(pairs.map((p) => p.right)) },
    );
  }, [pairs]);
  // Restore: answer_data holds the left→right matches.
  const savedPicks = useMemo<Record<string, string> | null>(() => {
    if (!saved) return null;
    try {
      const a = JSON.parse(saved.answer_data);
      if (a && typeof a === 'object') return a as Record<string, string>;
    } catch { /* not restorable */ }
    return null;
  }, [saved]);
  const [picks, setPicks] = useState<Record<string, string>>(savedPicks ?? {});
  // Вердикты и попытки по строкам (lib/matching.ts). Сохранённый ответ — все строки оценены.
  // ⚠️ Счётчик попыток живёт только здесь: форма answer_data ({"слово":"определение"}) не
  // меняется, gradeMatching на сервере сверяет число пар, и лишний ключ сломал бы проверку.
  // После перезагрузки страницы счётчик начинается заново — попыток больше, не меньше.
  const [rows, setRows] = useState<RowState>(() =>
    savedPicks ? gradeRows(pairs, savedPicks, emptyRows()) : emptyRows(),
  );
  // Подсказка «Нажмите строку, затем слово» — до первого действия ученика. «Строку», а не «определение»:
  // в строках бывают и окончания вопросов, и переводы, не только определения (владелец, 30.09.2026).
  const [touched, setTouched] = useState(false);
  // Сохранённый ответ приходит с сервера позже первого рендера, а начальное значение
  // useState берётся только один раз — без этого прошлый ответ не восстанавливался.
  // ⚠️ ТОЛЬКО ДО ПЕРВОГО ДЕЙСТВИЯ. После своей же отправки родитель может передать новый
  // `saved`; оценка с нуля обнулила бы счётчик попыток, и третья ошибка не наступала бы никогда.
  useEffect(() => {
    if (!savedPicks || touched) return;
    setPicks(savedPicks);
    setRows(gradeRows(pairs, savedPicks, emptyRows()));
  }, [savedPicks, pairs, touched]);
  // Выбор, ждущий пары: строка (right) или слово банка (left).
  const [sel, setSel] = useState<{ side: 'left' | 'right'; val: string } | null>(null);
  const [overRow, setOverRow] = useState<string | null>(null);
  const scope = useId();
  const ask = useConfirm();

  const rightToLeft = useMemo(() => rightOwner(picks), [picks]);
  // Верное слово строки — показывается только после третьей ошибки (`revealed`).
  const correctWord = useMemo(
    () => Object.fromEntries(pairs.map((p) => [p.right, p.left])) as Record<string, string>,
    [pairs],
  );
  const leftWords = useMemo(() => lefts.map((p) => p.left), [lefts]);
  const bank = bankWords(leftWords, picks);

  // Правила связывания — lib/matching.ts, покрыты проверками: этот кусок ломается тихо —
  // на экране просто сбрасывается выделение, а ответ уезжает.
  const tapWord = (word: string) => {
    setTouched(true);
    const next = matchTap({ picks, sel }, 'left', word);
    setPicks(next.picks);
    setSel(next.sel);
  };
  // Касание строки. Красная (329) сначала отдаёт в банк только своё слово; затем обычное
  // касание: выбрано слово — ложится сюда, нет — строка выделяется и ждёт слова.
  const tapRowCell = (row: string) => {
    if (rowLocked(rows, row)) return;
    setTouched(true);
    const base = resetRow(picks, rows, row);
    const next = matchTap({ picks: base.picks, sel }, 'right', row);
    setRows(base.rows);
    setPicks(next.picks);
    setSel(next.sel);
  };
  // Мышью: слово бросают на строку — тот же путь, что и касание «слово → строка».
  const dropOnRow = (row: string, token: string) => {
    const i = readDragToken(scope, token);
    const word = i === null ? undefined : leftWords[i];
    if (!word || rowLocked(rows, row)) return;
    setTouched(true);
    const base = resetRow(picks, rows, row);
    const next = matchTap({ picks: base.picks, sel: { side: 'left', val: word } }, 'right', row);
    setRows(base.rows);
    setPicks(next.picks);
    setSel(null);
  };
  // Бросок обратно в банк: только слово из неоценённой строки.
  const dropOnBank = (token: string) => {
    const i = readDragToken(scope, token);
    const word = i === null ? undefined : leftWords[i];
    if (!word || !picks[word] || rows.verdicts[picks[word]]) return;
    const next = { ...picks };
    delete next[word];
    setPicks(next);
    setSel(null);
  };

  const allCorrect = allPairsCorrect(pairs, picks);
  const ready = canCheck(rights, picks, rows);
  const settled = allSettled(rights, rows);
  const graded = Object.keys(rows.verdicts).length > 0;
  const hasBad = rights.some((r) => rows.verdicts[r] === 'bad');

  const submit = () => {
    if (!ready) return;
    setRows(gradeRows(pairs, picks, rows));
    setSel(null);
    onAnswer?.({ answer_data: JSON.stringify(picks), is_correct: allCorrect });
  };
  // «Сбросить всё» — отдельно и с подтверждением (решение владельца 28.09, п. 329).
  // Пересдача карточек: новая раскладка не должна сводиться к запоминанию позиций.
  const resetAll = async () => {
    const ok = await ask({
      message: t('blockRenderers.resetAllConfirm'),
      confirmLabel: t('blockRenderers.resetAll'),
    });
    if (!ok) return;
    setPicks({});
    setRows(emptyRows());
    setSel(null);
    setOrder(deal());
  };

  return (
    <div className="space-y-3">
      {c.instruction && <RichText text={c.instruction} className="font-medium text-on-surface" />}
      {!touched && !graded && Object.keys(picks).length === 0 && (
        <p className="text-sm text-on-surface-variant">{t('blockRenderers.matchHint')}</p>
      )}
      {/* Банк: только слова без пары — выбранное слово исчезает. Сюда же слово бросают мышью обратно. */}
      {bank.length > 0 && (
        <div
          className="ex-bank"
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); dropOnBank(e.dataTransfer.getData('text/plain')); }}
        >
          {bank.map((word) => (
            <DragChip
              key={word}
              value={word}
              payload={dragToken(scope, leftWords.indexOf(word))}
              selected={sel?.side === 'left' && sel.val === word}
              onClick={() => tapWord(word)}
            />
          ))}
        </div>
      )}
      {/* ⚠️ ОДНА КОЛОНКА НА ВСЕХ ШИРИНАХ: определение слева, поле со словом справа.
          Прежде слова шли плашками сверху, описания списком ниже, и на телефоне было
          непонятно, что делать (владелец, 30.09.2026). Две узкие колонки тоже не годятся:
          длинное описание вытягивалось в столбик (проверяющая 10.09.2026). */}
      <div className="space-y-2">
        {rights.map((r) => {
          const word = rightToLeft[r];
          const verdict = rows.verdicts[r];
          const locked = rowLocked(rows, r);
          const selected = sel?.side === 'right' && sel.val === r;
          // Строку с неоценённым словом можно тянуть мышью на другую строку или в банк.
          const movable = !!word && !verdict;
          return (
            <button
              key={r}
              type="button"
              disabled={locked}
              draggable={movable}
              onDragStart={(e) => { if (word) e.dataTransfer.setData('text/plain', dragToken(scope, leftWords.indexOf(word))); }}
              onDragOver={(e) => { if (!locked) { e.preventDefault(); setOverRow(r); } }}
              onDragLeave={() => setOverRow((o) => (o === r ? null : o))}
              onDrop={(e) => { e.preventDefault(); setOverRow(null); dropOnRow(r, e.dataTransfer.getData('text/plain')); }}
              onClick={() => tapRowCell(r)}
              className={cn(
                'ex-mcell w-full gap-3 transition',
                (verdict === 'ok') && 'ex-mcell--ok',
                (verdict === 'bad' || verdict === 'revealed') && 'ex-mcell--bad',
                !verdict && (selected || overRow === r) && 'ex-mcell--sel',
                !locked && 'cursor-pointer',
              )}
            >
              <span className="min-w-0 flex-1">
                {r}
                {verdict === 'revealed' && (
                  <span className="mt-1 block text-xs font-semibold text-success">✓ {correctWord[r]}</span>
                )}
              </span>
              <span
                className={cn(
                  'ex-gap flex min-w-[6.5rem] max-w-[45%] shrink-0 items-center justify-center gap-1 px-2 text-center text-sm',
                  !word && 'text-on-surface-variant',
                  !verdict && !word && sel?.side === 'left' && 'ex-gap--target',
                  verdict === 'ok' && 'ex-gap--ok',
                  (verdict === 'bad' || verdict === 'revealed') && 'ex-gap--bad',
                )}
              >
                <span className="min-w-0 break-words">{word ?? t('blockRenderers.matchPick')}</span>
                {verdict === 'bad' && (
                  <span className="material-symbols-outlined text-base" aria-label={t('blockRenderers.matchClearRow')}>close</span>
                )}
              </span>
            </button>
          );
        })}
      </div>
      {graded && !settled && hasBad && (
        <p className="text-sm text-error">{t('blockRenderers.matchFixRows')}</p>
      )}
      {settled && (
        <p className={allCorrect ? 'text-success' : 'text-error'}>
          {allCorrect ? t('blockRenderers.correct') : t('blockRenderers.matchRevealed')}
        </p>
      )}
      <div className="flex flex-wrap items-center gap-2">
        {!settled && (
          <button type="button" className="ex-check disabled:opacity-50" onClick={submit} disabled={!ready}>
            {t('blockRenderers.check')}
          </button>
        )}
        {graded && !allCorrect && (
          <Button size="sm" variant="ghost" onClick={() => void resetAll()}>
            <span className="material-symbols-outlined text-base">refresh</span>
            {t('blockRenderers.resetAll')}
          </Button>
        )}
      </div>
    </div>
  );
};

// =============================================================
// Vocabulary
// =============================================================

/** `limit` — дневной лимит новых слов: повтор до завтра не поможет (lib/addWordsQueue.ts). */
type CardState = 'idle' | 'busy' | 'done' | 'error' | 'limit';

const WordCardSetBlock: Renderer = ({ block }) => {
  const { t } = useTranslation();
  // ⚠️ Поле `voice` из содержимого блока больше не используется: оно задавало
  // язык системному синтезу, а системного синтеза здесь нет — всё звучит
  // нашим голосом.
  const c = block.content as { words?: { word: string; translation: string; example?: string }[] };
  const items = c.words ?? [];
  const [state, setState] = useState<Record<number, CardState>>({});
  // «Слишком часто» у одиночной кнопки: через сколько секунд можно снова (сервер говорит `retry_after`).
  const [waitSec, setWaitSec] = useState<Record<number, number | undefined>>({});
  const [running, setRunning] = useState(false);
  const [limitHit, setLimitHit] = useState(false);
  // Кэш ссылок в пределах открытого блока: одно слово ученик слушает по
  // нескольку раз, и дёргать сервер каждый раз незачем.
  //
  // ⚠️ Объявлен ДО раннего выхода намеренно. Раньше стоял после `return null`
  // для пустого списка: при пустом списке хук не вызывался, а стоило словам
  // появиться — число хуков между отрисовками менялось, и React падал.
  // Все хуки вызываются безусловно и в одном порядке. Нашёл линтер 16.08.2026.
  // Произношения берём ЗАРАНЕЕ, одним запросом на весь блок: нажатие должно
  // проигрывать готовое, а не ждать сервер (см. lib/wordAudio.ts).
  // В предпросмотре конструктора смотрит сотрудник под своей учёткой: «в словарь»
  // положило бы слова в ЕГО словарь, а предзагрузка ходит в ручку ученика. Молчим.
  const sandbox = usePreviewSandbox();
  useEffect(() => {
    if (items.length === 0 || sandbox) return;
    void preloadWords(items.map((w) => w.word));
  }, [items, sandbox]);

  if (items.length === 0) return null;

  // Свой блок — сервер возьмёт перевод из этого задания (vocab-word-source.md).
  const addWord = (i: number) => vocabApi.addToMyList(items[i].word, { sourceBlockId: block.id });

  const addOne = async (i: number) => {
    if (state[i] === 'done' || state[i] === 'busy' || running) return;
    setState((s) => ({ ...s, [i]: 'busy' }));
    setWaitSec((w) => ({ ...w, [i]: undefined }));
    try {
      await addWord(i);
      setState((s) => ({ ...s, [i]: 'done' }));
    } catch (err) {
      const f = classifyWordAddError(err);
      if (f.kind === 'limit') setLimitHit(true);
      if (f.kind === 'tooFast') setWaitSec((w) => ({ ...w, [i]: f.retryAfterSec }));
      setState((s) => ({ ...s, [i]: f.kind === 'limit' ? 'limit' : 'error' }));
    }
  };

  // «Добавить все»: только ещё не добавленные — повторно не шлём (слово владельца 18.10.2026), очередью по
  // 4 запроса; на «слишком часто» очередь ждёт сама; слово на дневном лимите остаётся с кнопкой, известные слова
  // идут дальше — сервер их принимает (lib/addWordsQueue.ts).
  const addAll = async () => {
    if (running) return;
    const todo = items.map((_, i) => i).filter((i) => state[i] !== 'done' && state[i] !== 'busy');
    if (todo.length === 0) return;
    setRunning(true);
    setLimitHit(false);
    const { limitHit: hit } = await runAddQueue(
      todo,
      async (i) => {
        setState((s) => ({ ...s, [i]: 'busy' }));
        await addWord(i);
      },
      { onResult: (i, outcome) => setState((s) => ({ ...s, [i]: outcome })) },
    );
    setLimitHit(hit);
    setRunning(false);
  };

  const doneCount = items.filter((_, i) => state[i] === 'done').length;
  const allDone = doneCount === items.length;

  return (
    <Card className="border-2 border-info/25 space-y-1">
      {!sandbox && (
        <button
          type="button"
          onClick={addAll}
          disabled={allDone || running}
          className="mb-1 flex items-center gap-2 text-sm font-semibold text-primary transition hover:opacity-80 disabled:opacity-50"
        >
          <span className="material-symbols-outlined text-[20px]">{running ? 'progress_activity' : 'playlist_add'}</span>
          {allDone ? t('blockRenderers.allWordsAdded') : t('blockRenderers.addAllWords')}
        </button>
      )}
      {!sandbox && !allDone && (running || limitHit) && (
        <p className={cn('text-xs', limitHit ? 'text-on-surface-variant' : 'text-primary')} role="status">
          {limitHit
            ? t('blockRenderers.wordsLimitTomorrow', { done: doneCount, total: items.length })
            : t('blockRenderers.wordsAddedProgress', { done: doneCount, total: items.length })}
        </p>
      )}
      <ul className="divide-y divide-outline/40">
        {items.map((it, i) => {
          const st = state[i] ?? 'idle';
          return (
            <li key={i} className="flex items-center gap-3 py-3">
              {!sandbox && (
                <button
                  type="button"
                  onClick={() => addOne(i)}
                  title={st === 'done' ? t('blockRenderers.wordAdded') : t('blockRenderers.addToVocab')}
                  disabled={st === 'busy' || st === 'done' || running}
                  className={cn(
                    'shrink-0 transition',
                    st === 'done' ? 'text-success' : 'text-on-surface-variant hover:text-primary',
                  )}
                >
                  <span className="material-symbols-outlined">
                    {st === 'done' ? 'check_circle' : st === 'busy' ? 'progress_activity' : 'playlist_add'}
                  </span>
                </button>
              )}
              {/* Общая кнопка: значок ожидания, пока звук грузится, и повтор
                  при ошибке — как в словаре и в карточках. */}
              <AudioPlayButton word={it.word} className="shrink-0 text-on-surface-variant" />
              <div className="min-w-0">
                <p className="font-semibold leading-tight">{it.word}</p>
                <p className="text-sm leading-tight text-on-surface-variant">{it.translation}</p>
                {it.example && (
                  <p className="mt-0.5 text-xs italic text-on-surface-variant/80">{it.example}</p>
                )}
                {st === 'error' && (
                  <p className="text-xs text-error">
                    {waitSec[i] !== undefined
                      ? t('blockRenderers.wordTooFast', { seconds: waitSec[i] })
                      : t('blockRenderers.wordAddError')}
                  </p>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </Card>
  );
};

// Fisher-Yates — used by the ordering / anagram renderers above to shuffle
// presentation order without modifying the source array.
function shuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

// ⚠️ БЕЗ СОДЕРЖИМОГО (13.10.2026). Раньше незнакомый тип показывался сырым JSON —
// то есть ученику с правильными ответами. Так до сих пор делает приложение 1.1.1:
// новые типы ему сервер подменяет сам (courses block_compat.go, договор
// docs/CONTRACTS/block-types-1.1.2.md). Здесь — на будущее: следующий новый тип в
// этой сборке покажет просьбу обновиться, а не ответы.
const FallbackBlock: Renderer = () => {
  const { t } = useTranslation();
  return (
    <p className="text-sm text-on-surface-variant">
      {t(isStoreBuild() ? 'blockRenderers.unknownBlockApp' : 'blockRenderers.unknownBlockWeb')}
    </p>
  );
};

// ---- Registry ----

const renderers: Partial<Record<BlockType, Renderer>> = {
  image: ImageBlock,
  image_carousel: ImageCarouselBlock,
  gif: GifBlock,
  video: VideoBlock,
  audio: AudioBlock,
  audio_record: AudioRecordBlock,
  word_drag_to_gap: DragToGapBlock,
  word_drag_to_image: DragToImageBlock,
  words_to_pictures: WordsToPicturesBlock,
  word_form_image: DragToImageBlock,
  quiz_no_timer: QuizBlock,
  quiz_with_timer: QuizBlock,
  true_false_ng: TrueFalseBlock,
  word_input_gap: InputGapBlock,
  word_input_image: WordInputImageBlock,
  word_form_select: SelectFormBlock,
  sentence_order: SentenceOrderBlock,
  column_sort: ColumnSortBlock,
  paragraph_order: ParagraphOrderBlock,
  anagram: AnagramBlock,
  matching: MatchingBlock,
  word_card_set: WordCardSetBlock,
  gap_fill: GapFillBlock,
  table_fill: TableFillBlock,
  article: ArticleBlock,
  text: TextBlock,
  essay: EssayBlock,
  open_items: OpenItemsBlock,
  callout: CalloutBlock,
  link: LinkBlock,
  divider: DividerBlock,
  embed: EmbedBlock,
  pdf: PdfBlock,
  highlight: HighlightBlock,
};

export function renderBlock(props: RenderProps): ReactNode {
  const R = renderers[props.block.type] ?? FallbackBlock;
  return <R {...props} />;
}
